// ══════════════════════════════════════════════════════════
//  MessagesPage.jsx
// ══════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';

export function MessagesPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { joinConversation, leaveConversation, onNewMessage, emitTyping, onUserTyping, markMessagesRead, onMessagesRead } = useSocket() || {};
  const [convos,   setConvos]   = useState([]);
  const [messages, setMessages] = useState([]);
  const [readStatus, setReadStatus] = useState({});
  const [active,   setActive]   = useState(conversationId || null);
  const [body,     setBody]     = useState('');
  const [sending,  setSending]  = useState(false);
  const [typing,   setTyping]   = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [convSettings, setConvSettings] = useState({}); // Muted/archived status per conversation
  const typingTimeoutRef = useRef(null);
  const bottomRef = useRef(null);

  const filteredMessages = messages.filter(m =>
    m.body.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleMute = async (convId) => {
    const isMuted = convSettings[convId]?.is_muted;
    try {
      await API.patch(`/messages/${convId}/mute`, { isMuted: !isMuted });
      setConvSettings(prev => ({
        ...prev,
        [convId]: { ...prev[convId], is_muted: !isMuted }
      }));
    } catch (err) { console.error('Failed to mute:', err); }
  };

  const toggleArchive = async (convId) => {
    const isArchived = convSettings[convId]?.is_archived;
    try {
      await API.patch(`/messages/${convId}/archive`, { isArchived: !isArchived });
      setConvSettings(prev => ({
        ...prev,
        [convId]: { ...prev[convId], is_archived: !isArchived }
      }));
    } catch (err) { console.error('Failed to archive:', err); }
  };

  // Fetch conversations (initial + polling every 20 seconds as fallback)
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const fetchConvos = () => API.get('/messages').then(r => setConvos(r.data.conversations)).catch(()=>{});
    fetchConvos();
    const interval = setInterval(fetchConvos, 20000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch messages for active conversation (initial + polling every 8 seconds)
  useEffect(() => {
    if (!active) return;
    const fetchMessages = async () => {
      try {
        const { data } = await API.get(`/messages/${active}`);
        setMessages(data.messages || []);
      } catch (err) {
        if (err.response?.status !== 429) {
          console.error('Failed to fetch messages:', err);
        }
      }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 8000);
    return () => clearInterval(interval);
  }, [active]);

  // Join conversation room and listen for real-time messages
  useEffect(() => {
    if (!active || !joinConversation) return;
    joinConversation(active);

    // Listen for new messages - add with timeout to avoid race conditions
    const unsubMessage = onNewMessage?.(msg => {
      setTimeout(() => {
        setMessages(prev => {
          const exists = prev.some(m => m.id === msg.id);
          if (exists) return prev;
          return [...prev, { ...msg, first_name: msg.sender_first_name, last_name: msg.sender_last_name }];
        });
        bottomRef.current?.scrollIntoView();
      }, 500);
    });

    // Listen for typing indicators
    const unsubTyping = onUserTyping?.(({ userId, isTyping }) => {
      setTyping(prev => ({ ...prev, [userId]: isTyping }));
    });

    // Listen for read receipts
    const unsubRead = onMessagesRead?.(({ messageIds }) => {
      setReadStatus(prev => {
        const updated = { ...prev };
        messageIds?.forEach(id => { updated[id] = true; });
        return updated;
      });
    });

    return () => {
      unsubMessage?.();
      unsubTyping?.();
      unsubRead?.();
      leaveConversation?.(active);
    };
  }, [active, joinConversation, onNewMessage, onUserTyping, onMessagesRead, leaveConversation]);

  const sendMessage = async () => {
    if (!body.trim() || !active) return;
    setSending(true);
    emitTyping?.(active, false);
    const messageBody = body;
    try {
      const conv = convos.find(c => c.id === active);
      await API.post('/messages', {
        conversationId: active,
        recipientId: conv?.participant_1 === user.id ? conv?.participant_2 : conv?.participant_1,
        body: messageBody
      });
      setBody('');

      // Fetch fresh data from server
      const [messagesRes, convosRes] = await Promise.all([
        API.get(`/messages/${active}`),
        API.get('/messages')
      ]);

      setMessages(messagesRes.data.messages || []);
      setConvos(convosRes.data.conversations || []);
      bottomRef.current?.scrollIntoView();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    setBody(e.target.value);
    if (active) {
      emitTyping?.(active, true);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        emitTyping?.(active, false);
      }, 2000);
    }
  };

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';

  return (
    <div style={{ maxWidth:1100, margin:'0 auto', padding:'32px 48px 0', display:'flex', gap:0, height:'calc(100vh - 100px)' }}>
      {/* Sidebar */}
      <div style={{ width:300, flexShrink:0, borderRight:'1px solid var(--border)', paddingRight:0, overflowY:'auto' }}>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, padding:'0 0 16px 0', marginBottom:0 }}>Messages</h2>
        {!convos.length && <p style={{ fontSize:13, color:'var(--text-soft)', padding:'20px 0' }}>No conversations yet</p>}
        {convos.map(c => (
          <div key={c.id} style={{ position:'relative', marginBottom:4 }}>
            <div onClick={() => { setActive(c.id); navigate(`/messages/${c.id}`); }}
              style={{ padding:'14px 16px', borderRadius:12, cursor:'pointer', background:active===c.id?'var(--cherry-light)':'transparent', border:`1px solid ${active===c.id?'rgba(192,57,43,.2)':'transparent'}`, transition:'all .15s', opacity: convSettings[c.id]?.is_muted ? 0.6 : 1 }}>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'var(--cherry)', flexShrink:0, overflow:'hidden' }}>
                  {c.avatar_url ? <img src={`${API_BASE}${c.avatar_url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : c.first_name?.[0]}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13 }}>{c.first_name} {c.last_name} {convSettings[c.id]?.is_muted && '🔇'}</div>
                  {c.listing_title && <div style={{ fontSize:11, color:'var(--text-soft)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Re: {c.listing_title}</div>}
                  {c.last_message && <div style={{ fontSize:11, color:'var(--text-soft)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:2 }}>{c.last_message}</div>}
                </div>
                {parseInt(c.unread_count)>0 && <div style={{ width:18,height:18,borderRadius:'50%',background:'var(--cherry)',color:'white',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>{c.unread_count}</div>}
              </div>
            </div>
            {/* Mute/Archive buttons */}
            <div style={{ position:'absolute', top:8, right:8, display:'flex', gap:4 }}>
              <button onClick={(e) => { e.stopPropagation(); toggleMute(c.id); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, padding:'4px 8px', borderRadius:4, background:'rgba(0,0,0,.05)', hover: { background:'rgba(0,0,0,.1)' } }} title={convSettings[c.id]?.is_muted ? 'Unmute' : 'Mute'}>
                {convSettings[c.id]?.is_muted ? '🔇' : '🔕'}
              </button>
              <button onClick={(e) => { e.stopPropagation(); toggleArchive(c.id); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, padding:'4px 8px', borderRadius:4, background:'rgba(0,0,0,.05)', hover: { background:'rgba(0,0,0,.1)' } }} title={convSettings[c.id]?.is_archived ? 'Unarchive' : 'Archive'}>
                {convSettings[c.id]?.is_archived ? '📭' : '📬'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Chat area */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', paddingLeft:24 }}>
        {!active ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:'var(--text-soft)' }}>
            <div style={{ fontSize:48 }}>💬</div>
            <div style={{ fontSize:14 }}>Select a conversation</div>
          </div>
        ) : (
          <>
            {/* Search bar */}
            <div style={{ padding:'12px', borderBottom:'1px solid var(--border)', background:'white', position:'sticky', top:0 }}>
              <input
                type="text"
                placeholder="🔍 Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width:'100%', padding:'8px 12px', borderRadius:6, border:'1px solid var(--border)', fontSize:12, fontFamily:'inherit' }}
              />
            </div>
            <div style={{ flex:1, overflowY:'auto', paddingBottom:16, paddingTop:8 }}>
              {filteredMessages.length === 0 ? (
                <div style={{ textAlign:'center', padding:'40px 20px', color:'var(--text-soft)', fontSize:13 }}>
                  {searchQuery ? '🔍 No messages match your search' : ''}
                </div>
              ) : (
                filteredMessages.map((m,i) => {
                const mine = m.sender_id === user?.id;
                const isRead = readStatus[m.id] || m.is_read;
                return (
                  <div key={m.id} style={{ display:'flex', justifyContent:mine?'flex-end':'flex-start', marginBottom:8 }}>
                    <div style={{ maxWidth:'70%', padding:'10px 14px', borderRadius: mine?'16px 16px 4px 16px':'16px 16px 16px 4px', background:mine?'var(--cherry)':'white', color:mine?'white':'var(--text-primary)', fontSize:14, lineHeight:1.55, boxShadow:'var(--shadow-sm)', border:mine?'none':'1px solid var(--border)' }}>
                      {m.body}
                      {m.metadata?.orderId && (
                        <div style={{ marginTop:8 }}>
                          <button
                            onClick={() => navigate(`/orders/${m.metadata.orderId}`)}
                            style={{ fontSize:12, fontWeight:700, padding:'4px 10px', borderRadius:6, border:'1px solid', borderColor:mine?'rgba(255,255,255,0.5)':'var(--border)', background:mine?'rgba(255,255,255,0.15)':'var(--cream)', color:mine?'white':'var(--text-primary)', cursor:'pointer' }}>
                            🧾 View Order #{m.metadata.orderNumber}
                          </button>
                        </div>
                      )}
                      <div style={{ fontSize:10, opacity:.65, marginTop:4, textAlign:'right', display:'flex', alignItems:'center', justifyContent:'flex-end', gap:4 }}>
                        {format(new Date(m.created_at),'MMM d, h:mm a')}
                        {mine && <span style={{ fontSize:8 }}>{isRead ? '✓✓' : '✓'}</span>}
                      </div>
                    </div>
                  </div>
                );
              })
              )}
              <div ref={bottomRef} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, paddingBottom:24, paddingTop:12, borderTop:'1px solid var(--border)' }}>
              {Object.values(typing).some(Boolean) && (
                <div style={{ fontSize:11, color:'var(--text-soft)', fontStyle:'italic' }}>✍️ Someone is typing...</div>
              )}
              <div style={{ display:'flex', gap:10 }}>
                <input className="form-input" placeholder="Type a message..." value={body} onChange={handleInputChange} onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMessage()} style={{ flex:1 }} />
                <button className="btn btn-cherry" onClick={sendMessage} disabled={sending||!body.trim()}>Send</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  ProfilePage.jsx
// ══════════════════════════════════════════════════════════
export function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/users/${id}`)
      .then(r => setProfile(r.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ textAlign:'center', padding:'80px' }}><div style={{ fontSize:40 }} className="float-anim">🍒</div></div>;
  if (!profile) return null;

  const { user, listings, reviews } = profile;
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';

  return (
    <div style={{ maxWidth:1000, margin:'0 auto', padding:'40px 48px 80px' }}>
      <div style={{ display:'flex', gap:32, alignItems:'flex-start', marginBottom:40 }}>
        <div style={{ width:100, height:100, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0 }}>
          {user.avatar_url ? <img src={`${API_BASE}${user.avatar_url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : user.first_name?.[0]?.toUpperCase()}
        </div>
        <div style={{ flex:1 }}>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:6 }}>{user.first_name} {user.last_name}</h1>
          <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span className="stars">{'★'.repeat(Math.round(user.rating_avg||0))}</span>
              <span style={{ fontWeight:700 }}>{parseFloat(user.rating_avg||0).toFixed(1)}</span>
              <span style={{ color:'var(--text-soft)', fontSize:13 }}>({user.rating_count} reviews)</span>
            </div>
            {user.id_verified && <span className="pill pill-active">✅ ID Verified</span>}
            <span style={{ fontSize:13, color:'var(--text-soft)' }}>📍 {user.location_city}, {user.location_state}</span>
            <span style={{ fontSize:13, color:'var(--text-soft)' }}>Member since {format(new Date(user.created_at),'MMM yyyy')}</span>
          </div>
          {user.bio && <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.65, maxWidth:520 }}>{user.bio}</p>}
          <div style={{ display:'flex', gap:20, marginTop:14 }}>
            {[['📋',user.total_listings,'Listings'],['💰',user.total_sales,'Sales'],['🔑',user.total_rentals,'Rentals']].map(([i,v,l]) => (
              <div key={l} style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700 }}>{v||0}</div>
                <div style={{ fontSize:11, color:'var(--text-soft)' }}>{i} {l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {listings?.length > 0 && (
        <div style={{ marginBottom:40 }}>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:20 }}>Active Listings</h2>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
            {listings.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </div>
      )}

      {reviews?.length > 0 && (
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:20 }}>Reviews ({reviews.length})</h2>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {reviews.map(r => (
              <div key={r.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:'18px 20px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, color:'var(--cherry)', overflow:'hidden', flexShrink:0 }}>
                    {r.avatar_url ? <img src={`${API_BASE}${r.avatar_url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : r.first_name?.[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14 }}>{r.first_name} {r.last_name}</div>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span className="stars" style={{ fontSize:12 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</span>
                      <span style={{ fontSize:12, color:'var(--text-soft)' }}>{format(new Date(r.created_at),'MMM d, yyyy')}</span>
                    </div>
                  </div>
                  {r.listing_title && <div style={{ marginLeft:'auto', fontSize:12, color:'var(--text-soft)' }}>Re: {r.listing_title}</div>}
                </div>
                {r.body && <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.65 }}>{r.body}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  SettingsPage.jsx
// ══════════════════════════════════════════════════════════
import ListingCard from '../components/listings/ListingCard';

export function SettingsPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [tab,     setTab]     = useState('profile');
  const [form,    setForm]    = useState({ firstName: user?.firstName||'', lastName: user?.lastName||'', bio: '', phone: '', locationCity: user?.locationCity||'', locationState: user?.locationState||'' });
  const [pwForm,  setPwForm]  = useState({ current:'', password:'', confirm:'' });
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSavedMsg]= useState('');
  const [error,   setError]   = useState('');
  const [avatar,  setAvatar]  = useState(null);

  if (!user) { navigate('/login'); return null; }

  const saveProfile = async () => {
    setSaving(true); setError(''); setSavedMsg('');
    try {
      const { data } = await API.patch('/users/me', form);
      updateUser({ firstName: data.user.first_name, lastName: data.user.last_name });
      setSavedMsg('Profile updated!');
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('avatar', file);
    const { data } = await API.post('/upload/avatar', fd, { headers: { 'Content-Type':'multipart/form-data' } });
    updateUser({ avatarUrl: data.url });
    setSavedMsg('Avatar updated!');
  };

  const changePassword = async () => {
    if (pwForm.password !== pwForm.confirm) { setError("Passwords don't match"); return; }
    if (pwForm.password.length < 8) { setError("Password too short"); return; }
    setSaving(true); setError('');
    try {
      await API.post('/auth/change-password', { current: pwForm.current, password: pwForm.password });
      setSavedMsg('Password changed!');
      setPwForm({ current:'', password:'', confirm:'' });
    } catch (err) { setError(err.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';

  return (
    <div style={{ maxWidth:680, margin:'0 auto', padding:'40px 24px 80px' }}>
      <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:24 }}>Account Settings</h1>

      <div className="tab-bar">
        {[['profile','👤 Profile'],['password','🔑 Password'],['notifications','🔔 Notifications']].map(([id,label])=>(
          <button key={id} className={`tab-btn ${tab===id?'active':''}`} onClick={()=>setTab(id)}>{label}</button>
        ))}
      </div>

      {saved  && <div className="alert alert-success" style={{ marginBottom:16 }}>✅ {saved}</div>}
      {error  && <div className="alert alert-error"   style={{ marginBottom:16 }}>⚠️ {error}</div>}

      {tab === 'profile' && (
        <div>
          {/* Avatar */}
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:24, padding:'20px', background:'var(--cream)', borderRadius:14, border:'1px solid var(--border)' }}>
            <div style={{ width:72, height:72, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0 }}>
              {user.avatarUrl ? <img src={`${API_BASE}${user.avatarUrl}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : user.firstName?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight:700, marginBottom:6 }}>{user.firstName} {user.lastName}</div>
              <label className="btn btn-ghost btn-sm" style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
                📷 Change photo
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={uploadAvatar} />
              </label>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input className="form-input" value={form.firstName} onChange={e=>setForm(p=>({...p,firstName:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input className="form-input" value={form.lastName} onChange={e=>setForm(p=>({...p,lastName:e.target.value}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea className="form-textarea" placeholder="Tell people about yourself..." value={form.bio} onChange={e=>setForm(p=>({...p,bio:e.target.value}))} style={{ minHeight:80 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.locationCity} onChange={e=>setForm(p=>({...p,locationCity:e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" value={form.locationState} onChange={e=>setForm(p=>({...p,locationState:e.target.value}))} />
            </div>
          </div>
          <div style={{ marginTop:8 }}>
            <button className="btn btn-cherry" onClick={saveProfile} disabled={saving}>
              {saving ? <><span className="spinner"/>Saving...</> : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {tab === 'password' && (
        <div>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input className="form-input" type="password" value={pwForm.current} onChange={e=>setPwForm(p=>({...p,current:e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" placeholder="Min 8 characters" value={pwForm.password} onChange={e=>setPwForm(p=>({...p,password:e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" value={pwForm.confirm} onChange={e=>setPwForm(p=>({...p,confirm:e.target.value}))} />
          </div>
          <button className="btn btn-cherry" onClick={changePassword} disabled={saving || !pwForm.current || !pwForm.password}>
            {saving ? <><span className="spinner"/>Changing...</> : 'Change Password'}
          </button>
        </div>
      )}

      {tab === 'notifications' && (
        <div>
          <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:20 }}>Manage which emails you receive from Cherrypkd.</p>
          {[
            { label:'New message',           desc:'Someone sends you a message' },
            { label:'Booking confirmed',      desc:'A booking you\'re involved in is confirmed' },
            { label:'Order shipped',          desc:'An order has been shipped' },
            { label:'New bid on your item',   desc:'Someone bids on your auction listing' },
            { label:'Escrow released',        desc:'Your funds have been transferred' },
            { label:'Review received',        desc:'Someone leaves you a review' },
            { label:'Marketing & tips',       desc:'News, features, and seller tips' },
          ].map(n => (
            <label key={n.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>{n.label}</div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>{n.desc}</div>
              </div>
              <label className="toggle-wrap">
                <input type="checkbox" defaultChecked />
                <span className="toggle-slider" />
              </label>
            </label>
          ))}
          <button className="btn btn-cherry" style={{ marginTop:20 }}>Save Preferences</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  ScriptManager — standalone component used inside AdminPage
// ══════════════════════════════════════════════════════════
function ScriptManager() {
  const [scripts,   setScripts]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState('');
  const [form, setForm] = useState({ location:'head', label:'', code:'' });

  const LOCATIONS = [
    { value:'head',       label:'<head>',         icon:'🔝', desc:'Analytics, fonts, meta tags — loads before page renders' },
    { value:'body_start', label:'<body> top',      icon:'🔛', desc:'Chat widgets, top banners — loads at top of page body' },
    { value:'body_end',   label:'<body> bottom',   icon:'🔚', desc:'Tag managers, chat scripts — loads after page content' },
  ];

  const QUICK = [
    { name:'Google Analytics 4', location:'head', code:`<!-- Google Analytics 4 -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXXXXXX');\n</script>` },
    { name:'Google Tag Manager', location:'head', code:`<!-- Google Tag Manager -->\n<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':\nnew Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],\nj=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);\n})(window,document,'script','dataLayer','GTM-XXXXXXX');</script>` },
    { name:'Facebook Pixel',     location:'head', code:`<!-- Meta Pixel -->\n<script>\n!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(arguments)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window, document,'script',\n'https://connect.facebook.net/en_US/fbevents.js');\nfbq('init', 'YOUR_PIXEL_ID');\nfbq('track', 'PageView');\n</script>` },
    { name:'Hotjar',             location:'head', code:`<!-- Hotjar -->\n<script>\n(function(h,o,t,j,a,r){\nh.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};\nh._hjSettings={hjid:YOUR_HJID,hjsv:6};\na=o.getElementsByTagName('head')[0];\nr=o.createElement('script');r.async=1;\nr.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;\na.appendChild(r);\n})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');\n</script>` },
    { name:'Intercom (Chat)',    location:'body_end', code:`<!-- Intercom -->\n<script>\nwindow.intercomSettings = { api_base: "https://api-iam.intercom.io", app_id: "YOUR_APP_ID" };\n</script>\n<script>(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/YOUR_APP_ID';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();</script>` },
    { name:'Crisp (Chat)',       location:'body_end', code:`<!-- Crisp Chat -->\n<script>\nwindow.$crisp=[];\nwindow.CRISP_WEBSITE_ID="YOUR_WEBSITE_ID";\n(function(){d=document;s=d.createElement("script");\ns.src="https://client.crisp.chat/l.js";\ns.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();\n</script>` },
  ];

  useEffect(() => {
    API.get('/admin/scripts')
      .then(r => setScripts(r.data.scripts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!form.label.trim() || !form.code.trim()) return;
    setSaving(true);
    try {
      const { data } = await API.post('/admin/scripts', form);
      setScripts(prev => [...prev, data.script]);
      setForm({ location:'head', label:'', code:'' });
      setShowForm(false);
      setSaved('✅ Script saved and active');
      setTimeout(() => setSaved(''), 3000);
    } catch (err) {
      setSaved('❌ ' + (err.response?.data?.error || 'Save failed'));
    } finally { setSaving(false); }
  };

  const toggle = async (id, is_active) => {
    await API.patch(`/admin/scripts/${id}`, { is_active });
    setScripts(prev => prev.map(s => s.id === id ? { ...s, is_active } : s));
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this script?')) return;
    await API.delete(`/admin/scripts/${id}`);
    setScripts(prev => prev.filter(s => s.id !== id));
  };

  const useQuick = (q) => {
    setForm({ location: q.location, label: q.name, code: q.code });
    setShowForm(true);
  };

  const grouped = LOCATIONS.reduce((acc, loc) => {
    acc[loc.value] = scripts.filter(s => s.location === loc.value);
    return acc;
  }, {});

  return (
    <div style={{ maxWidth:860 }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:6, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:4 }}>Script Manager</h2>
          <p style={{ fontSize:13, color:'var(--text-soft)' }}>
            Add analytics, chat widgets, tracking pixels and any other scripts. They inject automatically into every page — no code edits needed.
          </p>
        </div>
        <button className="btn btn-cherry btn-sm" onClick={() => setShowForm(s => !s)}>
          {showForm ? '✕ Cancel' : '+ Add Script'}
        </button>
      </div>

      {saved && (
        <div className={`alert ${saved.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom:16, marginTop:12 }}>
          {saved}
        </div>
      )}

      {/* Add script form */}
      {showForm && (
        <div style={{ background:'white', border:'1.5px solid var(--cherry)', borderRadius:16, padding:24, marginBottom:24, marginTop:12 }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>New Script</div>

          {/* Quick insert */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Quick insert</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {QUICK.map(q => (
                <button key={q.name} onClick={() => useQuick(q)}
                  style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'white', fontSize:12, fontWeight:600, cursor:'pointer', transition:'all .15s', fontFamily:"'DM Sans',sans-serif" }}
                  onMouseEnter={e => { e.target.style.borderColor = 'var(--cherry)'; e.target.style.color = 'var(--cherry)'; }}
                  onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'inherit'; }}>
                  {q.name}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            {/* Label */}
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Label *</label>
              <input className="form-input" placeholder="e.g. Google Analytics 4"
                value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} />
            </div>

            {/* Location */}
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Inject Location *</label>
              <select className="form-select" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}>
                {LOCATIONS.map(l => (
                  <option key={l.value} value={l.value}>{l.icon} {l.label} — {l.desc}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Code */}
          <div className="form-group" style={{ margin:'0 0 16px' }}>
            <label className="form-label">Script Code *</label>
            <textarea
              value={form.code}
              onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
              placeholder={'Paste your <script> tag or any HTML here...'}
              style={{ width:'100%', minHeight:160, padding:'12px 14px', borderRadius:9, border:'1.5px solid var(--border)', fontFamily:'monospace', fontSize:12, lineHeight:1.7, outline:'none', resize:'vertical', background:'#1A1A1A', color:'#a8ff78' }}
              onFocus={e => e.target.style.borderColor = 'var(--cherry)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <div className="form-hint">Paste the full script tag exactly as provided by the service.</div>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-cherry" onClick={save} disabled={saving || !form.label.trim() || !form.code.trim()}>
              {saving ? <><span className="spinner" /> Saving...</> : '💾 Save Script'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setShowForm(false); setForm({ location:'head', label:'', code:'' }); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Scripts grouped by location */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40 }}><div className="float-anim" style={{ fontSize:32 }}>🍒</div></div>
      ) : (
        LOCATIONS.map(loc => (
          <div key={loc.value} style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <span style={{ fontSize:18 }}>{loc.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{loc.label}</div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>{loc.desc}</div>
              </div>
              <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-soft)', fontWeight:600 }}>
                {grouped[loc.value].length} script{grouped[loc.value].length !== 1 ? 's' : ''}
              </span>
            </div>

            {grouped[loc.value].length === 0 ? (
              <div style={{ padding:'16px 20px', borderRadius:12, border:'1.5px dashed var(--border)', fontSize:13, color:'var(--text-soft)', background:'var(--cream)' }}>
                No scripts in this location yet.{' '}
                <button onClick={() => { setForm(p => ({ ...p, location: loc.value })); setShowForm(true); }}
                  style={{ color:'var(--cherry)', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:13 }}>
                  Add one →
                </button>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {grouped[loc.value].map(s => (
                  <div key={s.id} style={{ background:'white', border:`1px solid ${s.is_active ? 'var(--border)' : '#eee'}`, borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, opacity: s.is_active ? 1 : 0.55 }}>
                    {/* Toggle */}
                    <label className="toggle-wrap" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={s.is_active} onChange={e => toggle(s.id, e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>

                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{s.label}</div>
                      <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--text-soft)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:480 }}>
                        {s.code.trim().slice(0, 80)}...
                      </div>
                    </div>

                    <span className={`pill ${s.is_active ? 'pill-active' : 'pill-pending'}`}>
                      {s.is_active ? 'Active' : 'Paused'}
                    </span>

                    <button onClick={() => remove(s.id)}
                      style={{ width:28, height:28, borderRadius:'50%', border:'1px solid var(--border)', background:'white', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'var(--text-soft)', transition:'all .15s' }}
                      onMouseEnter={e => { e.target.style.background = '#FDECEA'; e.target.style.color = 'var(--cherry)'; e.target.style.borderColor = 'var(--cherry)'; }}
                      onMouseLeave={e => { e.target.style.background = 'white'; e.target.style.color = 'var(--text-soft)'; e.target.style.borderColor = 'var(--border)'; }}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── Admin helper: dispute resolution card ─────────────────
function AdminDisputeCard({ dispute: d, onUpdate }) {
  const [mode, setMode] = useState(null); // null | 'resolve' | 'refund'
  const [resolution, setResolution] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!resolution.trim()) return;
    setLoading(true);
    try {
      let r;
      if (mode === 'refund') {
        r = await API.patch(`/admin/disputes/${d.id}/resolve-with-refund`, { resolution, refundAmount: parseFloat(refundAmount||0), status:'resolved' });
      } else {
        r = await API.patch(`/admin/disputes/${d.id}/resolve`, { status:'closed', resolution });
      }
      onUpdate(r.data.dispute || { ...d, status: mode==='refund' ? 'resolved' : 'closed' });
      setMode(null);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
    setLoading(false);
  }

  const statusBg = { open:'#FFF9E6', resolved:'#EAF5EC', closed:'#F3F4F6' };
  const statusColor = { open:'#C9930A', resolved:'#2D7A3A', closed:'#6B7280' };

  return (
    <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8, flexWrap:'wrap', gap:8 }}>
        <div style={{ fontWeight:700, fontSize:15 }}>{d.reason || 'Dispute'}</div>
        <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:100, background:statusBg[d.status]||'#F3F4F6', color:statusColor[d.status]||'#6B7280', textTransform:'capitalize' }}>{d.status}</span>
      </div>
      {d.description && <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom:8, lineHeight:1.5 }}>{d.description}</div>}
      <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom: d.status==='open' ? 12 : 0 }}>
        Opened by: <strong>{d.opened_by_name}</strong> · Against: <strong>{d.against_name}</strong>
        {d.reference_type && ` · ${d.reference_type}`}
        {d.created_at && ` · ${new Date(d.created_at).toLocaleDateString()}`}
      </div>
      {d.resolution && <div style={{ fontSize:13, color:'var(--text-mid)', fontStyle:'italic', marginTop:4 }}>Resolution: {d.resolution}</div>}

      {d.status === 'open' && mode === null && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button className="btn btn-cherry btn-sm" onClick={() => setMode('refund')}>⚖️ Resolve + Refund</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setMode('close')}>✅ Close Dispute</button>
        </div>
      )}

      {d.status === 'open' && mode !== null && (
        <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:8 }}>
          <textarea
            placeholder={`Resolution notes (sent to both parties)…`}
            value={resolution} onChange={e => setResolution(e.target.value)}
            rows={2}
            style={{ padding:'8px 10px', border:'1px solid var(--border)', borderRadius:8, fontSize:13, resize:'vertical', fontFamily:'inherit', width:'100%' }}
          />
          {mode === 'refund' && (
            <input
              type="number" min="0" step="0.01" placeholder="Refund amount $ (0 for no refund)"
              value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
              style={{ padding:'7px 10px', border:'1px solid var(--border)', borderRadius:8, fontSize:13, width:220 }}
            />
          )}
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-cherry btn-sm" onClick={submit} disabled={loading || !resolution.trim()}>
              {loading ? 'Saving…' : mode==='refund' ? '⚖️ Resolve + Refund' : '✅ Close Dispute'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setMode(null); setResolution(''); setRefundAmount(''); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Admin helper: trigger payout inline form ──────────────
function AdminTriggerPayout({ onDone }) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  function submit() {
    if (!userId || !amount) return;
    setLoading(true);
    API.post('/admin/payouts/trigger', { userId: parseInt(userId), amount: parseFloat(amount) })
      .then(() => { setOpen(false); setUserId(''); setAmount(''); onDone(); })
      .catch(err => alert(err.response?.data?.error || 'Failed'))
      .finally(() => setLoading(false));
  }

  if (!open) return (
    <button className="btn btn-cherry btn-sm" onClick={() => setOpen(true)}>+ Trigger Payout</button>
  );
  return (
    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', background:'#FFF9F4', border:'1px solid #F4C9B0', borderRadius:8, padding:'8px 12px' }}>
      <input placeholder="User ID" value={userId} onChange={e => setUserId(e.target.value)} style={{ width:90, padding:'4px 8px', border:'1px solid var(--border)', borderRadius:6, fontSize:13 }} />
      <input placeholder="Amount $" value={amount} onChange={e => setAmount(e.target.value)} type="number" min="0" step="0.01" style={{ width:100, padding:'4px 8px', border:'1px solid var(--border)', borderRadius:6, fontSize:13 }} />
      <button className="btn btn-cherry btn-sm" onClick={submit} disabled={loading}>
        {loading ? 'Sending…' : '✅ Send Payout'}
      </button>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

// ── Admin helper: return request card ─────────────────────
function AdminReturnCard({ ret, onUpdate }) {
  const [notes, setNotes] = useState(ret.admin_notes || '');
  const [loading, setLoading] = useState(false);

  function resolve(status) {
    setLoading(true);
    API.patch(`/admin/returns/${ret.id}`, { status, admin_notes: notes })
      .then(r => onUpdate(r.data.returnRequest))
      .catch(err => alert(err.response?.data?.error || 'Failed'))
      .finally(() => setLoading(false));
  }

  const statusColor = { pending:'#FFF9E6', approved:'#EAF5EC', rejected:'#FEE2E2' };
  const statusText  = { pending:'#C9930A', approved:'#2D7A3A', rejected:'#991B1B' };

  return (
    <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:10, padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10, flexWrap:'wrap', gap:8 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:15 }}>
            Order #{ret.order_id} — {ret.listing_title || 'Listing'}
          </div>
          <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:2 }}>
            Buyer: {ret.buyer_name} · Seller: {ret.seller_name} · {new Date(ret.created_at).toLocaleDateString()}
          </div>
        </div>
        <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:100, background:statusColor[ret.status]||'#F3F4F6', color:statusText[ret.status]||'#374151', textTransform:'capitalize' }}>
          {ret.status}
        </span>
      </div>
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Reason</div>
        <div style={{ fontSize:14, marginTop:2, textTransform:'capitalize' }}>{(ret.reason||'').replace(/_/g,' ')} — {ret.description}</div>
      </div>
      <div style={{ display:'flex', gap:10, marginBottom:8 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Refund Type</div>
          <div style={{ fontSize:14, marginTop:2, textTransform:'capitalize' }}>{(ret.refund_type||'').replace(/_/g,' ')}</div>
        </div>
        {ret.refund_amount && (
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Amount</div>
            <div style={{ fontSize:14, marginTop:2 }}>${parseFloat(ret.refund_amount).toFixed(2)}</div>
          </div>
        )}
      </div>
      {ret.status === 'pending' && (
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:10 }}>
          <textarea
            placeholder="Admin notes (visible to buyer)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            style={{ width:'100%', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:8, fontSize:13, resize:'vertical', fontFamily:'inherit' }}
          />
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-cherry btn-sm" onClick={() => resolve('approved')} disabled={loading}>
              ✅ Approve Return
            </button>
            <button className="btn btn-sm" onClick={() => resolve('rejected')} disabled={loading} style={{ background:'#FEE2E2', color:'#991B1B', border:'none', borderRadius:8, padding:'6px 14px', fontWeight:600, cursor:'pointer' }}>
              ✗ Reject
            </button>
          </div>
        </div>
      )}
      {ret.status !== 'pending' && ret.admin_notes && (
        <div style={{ marginTop:8, fontSize:13, color:'var(--text-soft)', fontStyle:'italic' }}>
          Notes: {ret.admin_notes}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  AdminPage.jsx  — Full analytics dashboard + Script Manager
// ══════════════════════════════════════════════════════════
export function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab,     setTab]     = useState('analytics');
  const [data,    setData]    = useState({});
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState('30');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (!['admin','superadmin'].includes(user.role)) { navigate('/'); return; }
    fetchTab(tab);
  }, [tab, user, period]);

  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaved, setSettingsSaved] = useState('');
  const [catForm, setCatForm] = useState({ label:'', icon:'📦', description:'', slug:'', sortOrder:0 });
  const [catEdit, setCatEdit] = useState(null);
  const [kwForm, setKwForm] = useState({ keyword:'', action:'flag' });
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [emailTestTo, setEmailTestTo] = useState('');
  const [emailTestTemplate, setEmailTestTemplate] = useState('welcome');
  const [emailTestSending, setEmailTestSending] = useState(false);
  const [emailTestMsg, setEmailTestMsg] = useState('');
  const [catDrill, setCatDrill] = useState(null);
  const [catDrillListings, setCatDrillListings] = useState([]);
  const [catDrillLoading, setCatDrillLoading] = useState(false);
  const [kpiDrill, setKpiDrill] = useState(null);
  const [kpiDrillData, setKpiDrillData] = useState([]);
  const [kpiDrillLoading, setKpiDrillLoading] = useState(false);
  const [signupsDrill, setSignupsDrill] = useState(false);
  const [revenueDrill, setRevenueDrill] = useState(false);
  const pendingFilter = useRef(null);
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  const openKpiDrill = async (type, url) => {
    setKpiDrill(type);
    setKpiDrillLoading(true);
    setKpiDrillData([]);
    try {
      const r = await API.get(url);
      const key = type === 'users' || type === 'signups' ? 'users'
        : type === 'listings' ? 'listings'
        : type === 'disputes' ? 'disputes'
        : 'orders';
      setKpiDrillData(r.data[key] || []);
    } catch { setKpiDrillData([]); }
    setKpiDrillLoading(false);
  };

  const fetchTab = (t) => {
    setLoading(true);
    const filter = pendingFilter.current;
    pendingFilter.current = null;
    const buildListingsUrl = () => {
      if (!filter) return '/admin/listings';
      const params = new URLSearchParams();
      if (filter.status)      params.set('status', filter.status);
      if (filter.listingType) params.set('listingType', filter.listingType);
      if (filter.sellerId)    params.set('sellerId', filter.sellerId);
      return `/admin/listings?${params}`;
    };
    const endpoints = {
      analytics:    () => API.get(`/admin/stats?period=${period}`),
      users:        () => API.get('/admin/users'),
      listings:     () => API.get(buildListingsUrl()),
      orders:       () => API.get('/admin/orders'),
      disputes:     () => API.get('/admin/disputes'),
      suggestions:  () => API.get('/admin/suggestions'),
      reviews:      () => API.get('/reviews/admin/pending'),
      promotions:   () => API.get('/promotions/admin/analytics'),
      'activity-logs': () => API.get('/admin/activity-logs?limit=50'),
      settings:     () => API.get('/admin/settings').then(r => { setSettingsForm(r.data.settings || {}); return r; }),
      categories:   () => API.get('/categories'),
      payouts:           () => API.get('/admin/payouts'),
      returns:           () => API.get('/admin/returns?status=pending'),
      reports:           () => API.get('/admin/reports/revenue'),
      verifications:     () => API.get('/admin/verifications'),
      moderation:        () => API.get('/admin/images/flagged'),
      keywords:          () => API.get('/admin/keywords'),
      'search-analytics':() => API.get('/admin/search-analytics'),
      backup:            () => API.get('/admin/backup/list'),
      emails:            () => API.get('/admin/emails'),
    };
    (endpoints[t] || endpoints.analytics)()
      .then(r => setData(r.data))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  };

  const banUser = async (id, ban, reason) => {
    await API.patch(`/admin/users/${id}/ban`, { ban, reason: reason || null });
    setData(p => ({ ...p, users: p.users?.map(u => u.id===id ? {...u,is_banned:ban,ban_reason:reason||null} : u) }));
  };

  const bulkBanUsers = async (ban) => {
    const reason = window.prompt(`Reason for ${ban ? 'banning' : 'unbanning'} ${selectedUsers.size} users?`);
    if (reason === null) return;
    await API.patch('/admin/users/bulk/ban', { userIds: Array.from(selectedUsers), ban, reason });
    setSelectedUsers(new Set());
    fetchTab('users');
  };

  const bulkChangeRole = async (newRole) => {
    if (!window.confirm(`Change role to "${newRole}" for ${selectedUsers.size} users?`)) return;
    await API.patch('/admin/users/bulk/role', { userIds: Array.from(selectedUsers), role: newRole });
    setSelectedUsers(new Set());
    fetchTab('users');
  };

  const exportActivityLogs = () => {
    const logs = data.logs || [];
    const csv = 'Time,User,Email,Action,Resource,Details\n' + logs.map(l =>
      `"${new Date(l.created_at).toISOString()}","${l.first_name} ${l.last_name}","${l.email}","${l.action}","${l.resource_type || '-'}","${JSON.stringify(l.details || {}).replace(/"/g, '""')}"`
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const exportUsers = () => {
    const users = data.users || [];
    const csv = 'Name,Email,Role,Status,Verified,Joined\n' + users.map(u =>
      `"${u.first_name} ${u.last_name}","${u.email}","${u.role}","${u.is_banned ? 'Banned' : 'Active'}","${u.id_verified ? 'Yes' : 'No'}","${new Date(u.created_at).toISOString().split('T')[0]}"`
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const removeListing = async (id) => {
    const reason = window.prompt('Reason for removing this listing?', 'Policy violation');
    if (reason === null) return; // cancelled
    await API.patch(`/admin/listings/${id}/remove`, { reason });
    setData(p => ({ ...p, listings: p.listings?.map(l => l.id===id ? {...l,status:'removed',flag_reason:reason} : l) }));
  };

  const restoreListing = async (id) => {
    await API.patch(`/admin/listings/${id}/restore`);
    setData(p => ({ ...p, listings: p.listings?.map(l => l.id===id ? {...l,status:'active',flag_reason:null} : l) }));
  };

  const featureListing = async (id, featured) => {
    await API.patch(`/admin/listings/${id}/feature`, { featured });
    setData(p => ({ ...p, listings: p.listings?.map(l => l.id===id ? {...l,is_featured:featured} : l) }));
  };

  if (!user || !['admin','superadmin'].includes(user.role)) return null;

  // ── Simple bar chart component (no library needed) ──────────
  const BarChart = ({ data: chartData = [], valueKey, labelKey, color = 'var(--cherry)', height = 120 }) => {
    if (!chartData.length) return <div style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-soft)', fontSize:13 }}>No data yet</div>;
    const max = Math.max(...chartData.map(d => parseFloat(d[valueKey]) || 0), 1);
    return (
      <div style={{ display:'flex', alignItems:'flex-end', gap:3, height, paddingTop:8 }}>
        {chartData.map((d, i) => {
          const val = parseFloat(d[valueKey]) || 0;
          const pct = (val / max) * 100;
          return (
            <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3, height:'100%', justifyContent:'flex-end' }}
              title={`${d[labelKey]}: ${val}`}>
              <div style={{ width:'100%', background:color, borderRadius:'3px 3px 0 0', height:`${Math.max(pct, 2)}%`, opacity: 0.8 + (i / chartData.length) * 0.2, transition:'height .3s' }} />
            </div>
          );
        })}
      </div>
    );
  };

  // ── Donut chart (CSS-based) ──────────────────────────────────
  const DonutChart = ({ segments = [] }) => {
    const total = segments.reduce((s, x) => s + x.value, 0) || 1;
    const colors = ['#C0392B','#1A5276','#2D7A3A','#C9930A','#7D3C98'];
    let cumulative = 0;
    const size = 120;
    const r = 40;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * r;

    return (
      <div style={{ display:'flex', alignItems:'center', gap:20 }}>
        <svg width={size} height={size} style={{ flexShrink:0 }}>
          {segments.map((seg, i) => {
            const pct = seg.value / total;
            const offset = circumference * (1 - cumulative);
            const dash   = circumference * pct;
            const el = (
              <circle key={i} cx={cx} cy={cy} r={r}
                fill="none" stroke={colors[i % colors.length]} strokeWidth={20}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={offset}
                style={{ transform:'rotate(-90deg)', transformOrigin:'center', transition:'all .4s' }}
              />
            );
            cumulative += pct;
            return el;
          })}
          <circle cx={cx} cy={cy} r={27} fill="white" />
        </svg>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {segments.map((seg, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:colors[i % colors.length], flexShrink:0 }} />
              <span style={{ color:'var(--text-mid)' }}>{seg.label}</span>
              <span style={{ fontWeight:700, marginLeft:'auto', minWidth:30, textAlign:'right' }}>{seg.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth:1280, margin:'0 auto', padding:'32px 48px 80px' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28, flexWrap:'wrap', gap:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:24 }}>🛡️</span>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700 }}>Admin Panel</h1>
        </div>
        {tab === 'analytics' && (
          <div style={{ display:'flex', gap:6 }}>
            {[['7','7 days'],['30','30 days'],['90','90 days']].map(([val,label]) => (
              <button key={val} className={`btn btn-sm ${period===val?'btn-cherry':'btn-ghost'}`}
                onClick={() => setPeriod(val)}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ marginBottom:28, overflowX:'auto', flexWrap:'nowrap', paddingBottom:2 }}>
        {[
          ['analytics',  '📊 Analytics'],
          ['users',      '👥 Users'],
          ['listings',   '📋 Listings'],
          ['orders',     '📦 Orders'],
          ['disputes',   '⚠️ Disputes'],
          ['reviews',    '⭐ Reviews'],
          ['promotions', '💰 Promotions'],
          ['activity-logs', '🔍 Activity Logs'],
          ['suggestions','🏷️ Suggestions'],
          ['scripts',    '🧩 Scripts'],
          ['categories', '🗂️ Categories'],
          ['payouts',    '💸 Payouts'],
          ['returns',    '↩️ Returns'],
          ['reports',    '📊 Reports'],
          ['verifications','🪪 ID Verify'],
          ['moderation', '🖼️ Images'],
          ['keywords',   '🚫 Keywords'],
          ['search-analytics','🔍 Search'],
          ['backup',     '💾 Backup'],
          ['emails',     '📧 Emails'],
          ['settings',   '⚙️ Settings'],
        ].map(([id,l]) => (
          <button key={id} className={`tab-btn ${tab===id?'active':''}`} onClick={() => setTab(id)}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px' }}>
          <div style={{ fontSize:40 }} className="float-anim">🍒</div>
        </div>
      ) : (
        <>
          {/* ── ANALYTICS TAB ── */}
          {tab === 'analytics' && (
            <div>
              {/* KPI cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:16, marginBottom:kpiDrill ? 0 : 28 }}>
                {[
                  { key:'users',    icon:'👥', label:'Total Users',     value: data.totalUsers?.toLocaleString() || 0,    sub:`+${data.newUsers||0} this period`, color:'#1A5276', url:'/admin/users', tab:'users' },
                  { key:'listings', icon:'📋', label:'Active Listings', value: data.activeListings?.toLocaleString() || 0, sub:'currently live',                   color:'#2D7A3A', url:'/admin/listings?status=active', tab:'listings' },
                  { key:'revenue',  icon:'💰', label:'Revenue',          value:`$${parseFloat(data.revenue||0).toLocaleString()}`, sub:`${data.period}d period`,   color:'#C0392B', url:'/admin/orders', tab:'orders' },
                  { key:'txns',     icon:'💳', label:'Transactions',    value: data.transactions?.toLocaleString() || 0,   sub:`last ${data.period} days`,         color:'#C9930A', url:'/admin/orders', tab:'orders' },
                  { key:'disputes', icon:'⚠️', label:'Open Disputes',   value: data.openDisputes?.toLocaleString() || 0,   sub:'need attention',                   color: data.openDisputes > 0 ? '#C0392B' : '#2D7A3A', url:'/admin/disputes?status=open', tab:'disputes' },
                ].map(s => (
                  <div key={s.key}
                    onClick={() => kpiDrill === s.key ? setKpiDrill(null) : openKpiDrill(s.key, s.url)}
                    style={{ background: kpiDrill===s.key ? s.color : 'white', borderRadius:14, border:`2px solid ${kpiDrill===s.key ? s.color : 'var(--border)'}`, padding:'20px', cursor:'pointer', transition:'box-shadow .15s, transform .15s, background .15s' }}
                    onMouseEnter={e => { if(kpiDrill!==s.key){ e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.08)'; e.currentTarget.style.transform='translateY(-1px)'; }}}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none'; }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                      <span style={{ fontSize:24 }}>{s.icon}</span>
                      <span style={{ fontSize:10, color: kpiDrill===s.key ? 'rgba(255,255,255,.7)' : 'var(--text-soft)', fontWeight:600 }}>{kpiDrill===s.key ? '▲ OPEN' : 'DRILL ↓'}</span>
                    </div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700, color: kpiDrill===s.key ? 'white' : s.color, marginBottom:4 }}>{s.value}</div>
                    <div style={{ fontSize:12, fontWeight:700, color: kpiDrill===s.key ? 'rgba(255,255,255,.9)' : 'var(--text-primary)', marginBottom:2 }}>{s.label}</div>
                    <div style={{ fontSize:11, color: kpiDrill===s.key ? 'rgba(255,255,255,.7)' : 'var(--text-soft)' }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* KPI drill-down panel */}
              {kpiDrill && (() => {
                const meta = {
                  users:    { label:'Total Users',     tab:'users',    icon:'👥' },
                  listings: { label:'Active Listings', tab:'listings', icon:'📋', filter:{ status:'active' } },
                  revenue:  { label:'Revenue',         tab:'orders',   icon:'💰' },
                  txns:     { label:'Transactions',    tab:'orders',   icon:'💳' },
                  disputes: { label:'Open Disputes',   tab:'disputes', icon:'⚠️' },
                }[kpiDrill];
                return (
                  <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:'0 0 14px 14px', padding:20, marginBottom:28, borderTop:'none' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setKpiDrill(null); setKpiDrillData([]); }}>← Back</button>
                      <span style={{ fontWeight:700, fontSize:14 }}>{meta.icon} {meta.label}</span>
                      {!kpiDrillLoading && <span style={{ fontSize:12, color:'var(--text-soft)' }}>({kpiDrillData.length} shown)</span>}
                      <button className="btn btn-cherry btn-sm" style={{ marginLeft:'auto' }}
                        onClick={() => { setKpiDrill(null); if(meta.filter) { pendingFilter.current = meta.filter; } setTab(meta.tab); }}>
                        View all in {meta.label} tab →
                      </button>
                    </div>
                    {kpiDrillLoading
                      ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>Loading…</p>
                      : kpiDrillData.length === 0
                        ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>No data found</p>
                        : <div style={{ overflowX:'auto', maxHeight:360, overflowY:'auto' }}>
                            {/* Users */}
                            {(kpiDrill === 'users') && (
                              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                                <thead><tr style={{ background:'var(--cream)', position:'sticky', top:0 }}>
                                  {['Name','Email','Role','Status','Joined'].map(h => <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', fontSize:11, textTransform:'uppercase' }}>{h}</th>)}
                                </tr></thead>
                                <tbody>{kpiDrillData.map(u => (
                                  <tr key={u.id} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                                    onClick={() => navigate(`/profile/${u.id}`)}
                                    onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'8px 12px', fontWeight:600 }}>{u.first_name} {u.last_name}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{u.email}</td>
                                    <td style={{ padding:'8px 12px' }}><span className="pill pill-pending" style={{ fontSize:10 }}>{u.role}</span></td>
                                    <td style={{ padding:'8px 12px' }}><span className={`pill ${u.is_banned ? 'pill-pending' : 'pill-active'}`} style={{ fontSize:10 }}>{u.is_banned ? 'Banned' : 'Active'}</span></td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            )}
                            {/* Active listings */}
                            {kpiDrill === 'listings' && (
                              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                                <thead><tr style={{ background:'var(--cream)', position:'sticky', top:0 }}>
                                  {['Title','Seller','Type','Price','Created'].map(h => <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', fontSize:11, textTransform:'uppercase' }}>{h}</th>)}
                                </tr></thead>
                                <tbody>{kpiDrillData.map(l => (
                                  <tr key={l.id} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                                    onClick={() => navigate(`/listing/${l.slug || l.id}`)}
                                    onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'8px 12px', fontWeight:600, maxWidth:200 }}>{l.title}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{l.first_name} {l.last_name}</td>
                                    <td style={{ padding:'8px 12px' }}><span className={`pill ${l.listing_type==='rent'?'pill-rent':l.listing_type==='auction'?'pill-auction':'pill-sale'}`} style={{ fontSize:10 }}>{l.listing_type}</span></td>
                                    <td style={{ padding:'8px 12px', fontWeight:700 }}>{l.price ? `$${parseFloat(l.price).toFixed(2)}` : '—'}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{new Date(l.created_at).toLocaleDateString()}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            )}
                            {/* Revenue / Transactions */}
                            {(kpiDrill === 'revenue' || kpiDrill === 'txns') && (
                              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                                <thead><tr style={{ background:'var(--cream)', position:'sticky', top:0 }}>
                                  {['Listing','Buyer','Seller','Amount','Status','Date'].map(h => <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', fontSize:11, textTransform:'uppercase' }}>{h}</th>)}
                                </tr></thead>
                                <tbody>{kpiDrillData.map(o => (
                                  <tr key={o.id} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                                    onClick={() => o.record_type !== 'booking' && navigate(`/orders/${o.id}`)}
                                    onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'8px 12px', fontWeight:600, maxWidth:180 }}>{o.title}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{o.buyer_first} {o.buyer_last}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{o.seller_first} {o.seller_last}</td>
                                    <td style={{ padding:'8px 12px', fontWeight:700, color:'var(--green)' }}>${parseFloat(o.total_charged||0).toFixed(2)}</td>
                                    <td style={{ padding:'8px 12px' }}><span className={`pill ${o.status==='completed'?'pill-paid':'pill-pending'}`} style={{ fontSize:10 }}>{o.status}</span></td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            )}
                            {/* Disputes */}
                            {kpiDrill === 'disputes' && (
                              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                                <thead><tr style={{ background:'var(--cream)', position:'sticky', top:0 }}>
                                  {['Opened By','Against','Reason','Status','Opened'].map(h => <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', fontSize:11, textTransform:'uppercase' }}>{h}</th>)}
                                </tr></thead>
                                <tbody>{kpiDrillData.map(d => (
                                  <tr key={d.id} style={{ borderBottom:'1px solid var(--border)' }}
                                    onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'8px 12px', fontWeight:600 }}>{d.opened_by_name}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{d.against_name}</td>
                                    <td style={{ padding:'8px 12px', maxWidth:200 }}>{d.reason}</td>
                                    <td style={{ padding:'8px 12px' }}><span className={`pill ${d.status==='open'?'pill-pending':'pill-active'}`} style={{ fontSize:10 }}>{d.status}</span></td>
                                    <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{new Date(d.created_at).toLocaleDateString()}</td>
                                  </tr>
                                ))}</tbody>
                              </table>
                            )}
                          </div>
                    }
                  </div>
                );
              })()}

              {/* Charts row */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, marginBottom:28 }}>

                {/* Signups chart */}
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  {!signupsDrill ? (
                    <>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>New Signups</div>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                          onClick={async () => { setSignupsDrill(true); await openKpiDrill('users', `/admin/users?since=${period}`); }}>
                          View list ↓
                        </button>
                      </div>
                      <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom:16 }}>Daily — last {period} days</div>
                      <BarChart data={data.signupsSeries||[]} valueKey="count" labelKey="date" color="#1A5276" />
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-soft)', marginTop:6 }}>
                        <span>{data.signupsSeries?.[0]?.date?.slice(5)}</span>
                        <span>{data.signupsSeries?.[data.signupsSeries.length-1]?.date?.slice(5)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setSignupsDrill(false); setKpiDrillData([]); }}>← Back</button>
                        <span style={{ fontWeight:700, fontSize:14 }}>👥 New Signups — last {period} days</span>
                        <button className="btn btn-cherry btn-sm" style={{ marginLeft:'auto', fontSize:11 }} onClick={() => { setSignupsDrill(false); setTab('users'); }}>View all users →</button>
                      </div>
                      {kpiDrillLoading
                        ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>Loading…</p>
                        : kpiDrillData.length === 0
                          ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>No signups in this period</p>
                          : <div style={{ maxHeight:300, overflowY:'auto' }}>
                              {kpiDrillData.map(u => (
                                <div key={u.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 4px', borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                                  onClick={() => navigate(`/profile/${u.id}`)}
                                  onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                  <div style={{ width:28, height:28, borderRadius:'50%', background:'#1A5276', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0 }}>
                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                  </div>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:12, fontWeight:600 }}>{u.first_name} {u.last_name}</div>
                                    <div style={{ fontSize:11, color:'var(--text-soft)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                                  </div>
                                  <div style={{ fontSize:11, color:'var(--text-soft)', flexShrink:0 }}>{new Date(u.created_at).toLocaleDateString()}</div>
                                </div>
                              ))}
                            </div>
                      }
                    </>
                  )}
                </div>

                {/* Revenue chart */}
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  {!revenueDrill ? (
                    <>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>Revenue</div>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                          onClick={() => setRevenueDrill(true)}>
                          View breakdown ↓
                        </button>
                      </div>
                      <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom:16 }}>Daily gross — last {period} days</div>
                      <BarChart data={data.revenueSeries||[]} valueKey="revenue" labelKey="date" color="#C0392B" />
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-soft)', marginTop:6 }}>
                        <span>{data.revenueSeries?.[0]?.date?.slice(5)}</span>
                        <span>{data.revenueSeries?.[data.revenueSeries.length-1]?.date?.slice(5)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setRevenueDrill(false)}>← Back</button>
                        <span style={{ fontWeight:700, fontSize:14 }}>💰 Revenue — last {period} days</span>
                        <button className="btn btn-cherry btn-sm" style={{ marginLeft:'auto', fontSize:11 }} onClick={() => { setRevenueDrill(false); setTab('orders'); }}>View all orders →</button>
                      </div>
                      {!(data.revenueSeries||[]).length
                        ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>No revenue in this period</p>
                        : <div style={{ maxHeight:300, overflowY:'auto' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                              <thead><tr style={{ background:'var(--cream)', position:'sticky', top:0 }}>
                                {['Date','Gross Revenue','Platform Fees','Transactions'].map(h => (
                                  <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', fontSize:11, textTransform:'uppercase' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {[...(data.revenueSeries||[])].reverse().map(r => (
                                  <tr key={r.date} style={{ borderBottom:'1px solid var(--border)' }}
                                    onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'8px 12px', fontWeight:600 }}>{new Date(r.date).toLocaleDateString()}</td>
                                    <td style={{ padding:'8px 12px', fontWeight:700, color:'var(--green)' }}>${parseFloat(r.revenue||0).toFixed(2)}</td>
                                    <td style={{ padding:'8px 12px', color:'var(--cherry)' }}>${parseFloat(r.fees||0).toFixed(2)}</td>
                                    <td style={{ padding:'8px 12px' }}>{r.transactions}</td>
                                  </tr>
                                ))}
                                <tr style={{ background:'var(--cream)', fontWeight:700, fontSize:12 }}>
                                  <td style={{ padding:'8px 12px' }}>Total</td>
                                  <td style={{ padding:'8px 12px', color:'var(--green)' }}>${(data.revenueSeries||[]).reduce((s,r) => s + parseFloat(r.revenue||0), 0).toFixed(2)}</td>
                                  <td style={{ padding:'8px 12px', color:'var(--cherry)' }}>${(data.revenueSeries||[]).reduce((s,r) => s + parseFloat(r.fees||0), 0).toFixed(2)}</td>
                                  <td style={{ padding:'8px 12px' }}>{(data.revenueSeries||[]).reduce((s,r) => s + parseInt(r.transactions||0), 0)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                      }
                    </>
                  )}
                </div>

                {/* Listings by type */}
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>Listings by Type</div>
                  <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom:20 }}>Active listings breakdown — click to filter</div>
                  <DonutChart segments={(data.listingsByType||[]).map(r => ({
                    label: r.listing_type === 'rent' ? '🔵 Rentals' : r.listing_type === 'auction' ? '🔴 Auctions' : r.listing_type === 'barter' ? '🟡 Barter' : r.listing_type === 'free' ? '🟣 Free' : '🟢 For Sale',
                    value: parseInt(r.count)
                  }))} />
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:14, justifyContent:'center' }}>
                    {(data.listingsByType||[]).map(r => (
                      <button key={r.listing_type} className="btn btn-ghost btn-sm"
                        style={{ fontSize:11 }}
                        onClick={() => { pendingFilter.current = { listingType: r.listing_type }; setTab('listings'); }}>
                        {r.listing_type === 'rent' ? '🔵' : r.listing_type === 'auction' ? '🔴' : r.listing_type === 'barter' ? '🟡' : r.listing_type === 'free' ? '🟣' : '🟢'} {r.listing_type} ({r.count})
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Second row */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:28 }}>

                {/* Top categories */}
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  {!catDrill ? (
                    <>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:16 }}>Top Categories</div>
                      {(data.listingsByCategory||[]).length === 0
                        ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>No listings yet</p>
                        : (data.listingsByCategory||[]).map((c) => {
                            const max = parseInt(data.listingsByCategory[0].count) || 1;
                            return (
                              <div key={c.id || c.label} style={{ marginBottom:10, cursor:'pointer', borderRadius:8, padding:'4px 6px', transition:'background .15s' }}
                                onClick={async () => {
                                  setCatDrill(c);
                                  setCatDrillLoading(true);
                                  setCatDrillListings([]);
                                  try {
                                    const r = await API.get(`/admin/listings?categoryId=${c.id}&status=active`);
                                    setCatDrillListings(r.data.listings || []);
                                  } catch { setCatDrillListings([]); }
                                  setCatDrillLoading(false);
                                }}
                                onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:4 }}>
                                  <span>{c.icon} {c.label}</span>
                                  <span style={{ fontWeight:700 }}>{c.count}</span>
                                </div>
                                <div style={{ height:6, background:'var(--cream)', borderRadius:3 }}>
                                  <div style={{ height:'100%', width:`${(parseInt(c.count)/max)*100}%`, background:'var(--cherry)', borderRadius:3, transition:'width .4s' }} />
                                </div>
                              </div>
                            );
                          })
                      }
                    </>
                  ) : (
                    <>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setCatDrill(null); setCatDrillListings([]); }}>← Back</button>
                        <div style={{ fontWeight:700, fontSize:14 }}>{catDrill.icon} {catDrill.label}</div>
                        <span style={{ fontSize:12, color:'var(--text-soft)', marginLeft:'auto' }}>{catDrillListings.length} active listing{catDrillListings.length !== 1 ? 's' : ''}</span>
                      </div>
                      {catDrillLoading
                        ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>Loading…</p>
                        : catDrillListings.length === 0
                          ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>No active listings in this category</p>
                          : <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:340, overflowY:'auto' }}>
                              {catDrillListings.map(l => (
                                <div key={l.id} style={{ display:'flex', gap:10, alignItems:'center', padding:'8px 10px', borderRadius:8, border:'1px solid var(--border)', cursor:'pointer', background:'white' }}
                                  onClick={() => navigate(`/listing/${l.slug || l.id}`)}>
                                  <div style={{ width:36, height:36, borderRadius:6, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                                    {l.primary_image
                                      ? <img src={`${API_BASE}${l.primary_image}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                                      : '📦'}
                                  </div>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.title}</div>
                                    <div style={{ fontSize:11, color:'var(--text-soft)' }}>{l.first_name} {l.last_name} · {l.listing_type}</div>
                                  </div>
                                  <div style={{ fontSize:13, fontWeight:700, flexShrink:0 }}>
                                    {l.price ? `$${parseFloat(l.price).toFixed(2)}` : '—'}
                                  </div>
                                </div>
                              ))}
                            </div>
                      }
                    </>
                  )}
                </div>

                {/* Recent transactions */}
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>Recent Transactions</div>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={() => setTab('orders')}>View all →</button>
                  </div>
                  {!(data.recentTxns||[]).length
                    ? <p style={{ fontSize:13, color:'var(--text-soft)' }}>No transactions yet</p>
                    : (data.recentTxns||[]).map(t => (
                        <div key={t.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:10, marginBottom:10, borderBottom:'1px solid var(--border)', cursor: t.reference_type==='order' ? 'pointer' : 'default', borderRadius:6, padding:'6px 4px' }}
                          onClick={() => t.reference_type === 'order' && navigate(`/orders/${t.reference_id}`)}
                          onMouseEnter={e => t.reference_type === 'order' && (e.currentTarget.style.background='var(--cream)')}
                          onMouseLeave={e => t.reference_type === 'order' && (e.currentTarget.style.background='transparent')}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600 }}>{t.first_name} {t.last_name}</div>
                            <div style={{ fontSize:11, color:'var(--text-soft)' }}>
                              {t.payment_method} · {new Date(t.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontWeight:700, color:'var(--green)' }}>${parseFloat(t.gross_amount||0).toFixed(2)}</div>
                            <span className={`pill ${t.status==='completed'?'pill-paid':t.status==='held_escrow'?'pill-escrow':'pill-pending'}`} style={{ fontSize:10 }}>
                              {t.status}
                            </span>
                          </div>
                        </div>
                      ))
                  }
                </div>
              </div>

              {/* Top sellers */}
              {(data.topSellers||[]).length > 0 && (
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:16 }}>Top Sellers</div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'var(--cream)' }}>
                        {['Seller','Email','Total Listings','Active Listings','Rating'].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.topSellers.map(s => (
                        <tr key={s.id} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background .12s' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'10px 12px', fontWeight:600 }} onClick={() => navigate(`/profile/${s.id}`)}>{s.first_name} {s.last_name}</td>
                          <td style={{ padding:'10px 12px', color:'var(--text-soft)' }} onClick={() => navigate(`/profile/${s.id}`)}>{s.email}</td>
                          <td style={{ padding:'10px 12px' }} onClick={() => { pendingFilter.current = { sellerId: s.id }; setTab('listings'); }}
                            title="Click to see all listings by this seller">
                            <span style={{ color:'var(--cherry)', fontWeight:600 }}>{s.listing_count}</span>
                          </td>
                          <td style={{ padding:'10px 12px' }} onClick={() => { pendingFilter.current = { sellerId: s.id, status: 'active' }; setTab('listings'); }}
                            title="Click to see active listings by this seller">
                            <span style={{ color:'var(--green)', fontWeight:600 }}>{s.active_count}</span>
                          </td>
                          <td style={{ padding:'10px 12px' }} onClick={() => navigate(`/profile/${s.id}`)}>
                            {s.rating_count > 0
                              ? <span>⭐ {parseFloat(s.rating_avg).toFixed(1)} ({s.rating_count})</span>
                              : <span style={{ color:'var(--text-soft)' }}>No reviews</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── USERS TAB ── */}
          {tab === 'users' && (
            <div>
              {/* Search + filters + export */}
              <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
                <input className="form-input" placeholder="Search by name or email..."
                  style={{ flex:1, minWidth:200 }}
                  onChange={e => {
                    const q = e.target.value;
                    clearTimeout(window._userSearch);
                    window._userSearch = setTimeout(() => {
                      API.get(`/admin/users?q=${encodeURIComponent(q)}`).then(r => setData(p => ({...p, users: r.data.users})));
                    }, 400);
                  }} />
                <button className="btn btn-sm btn-ghost" onClick={exportUsers} title="Export as CSV">📥 Export</button>
              </div>

              {/* Bulk actions */}
              {selectedUsers.size > 0 && (
                <div style={{ marginBottom:20, padding:12, background:'var(--cherry-light)', borderRadius:8, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
                  <span style={{ fontWeight:600, fontSize:13 }}>✓ {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected</span>
                  <button className="btn btn-sm btn-ghost" onClick={() => bulkChangeRole('user')}>Make User</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => bulkChangeRole('admin')}>Make Admin</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => bulkBanUsers(true)} style={{ background:'#FDECEA', color:'var(--cherry)' }}>Ban</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => bulkBanUsers(false)}>Unban</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setSelectedUsers(new Set())}>Clear</button>
                </div>
              )}

              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:700 }}>
                  <thead>
                    <tr style={{ background:'var(--cream)', textAlign:'left' }}>
                      <th style={{ padding:'10px 14px', width:40 }}>
                        <input type="checkbox"
                          checked={selectedUsers.size === (data.users||[]).length && (data.users||[]).length > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedUsers(new Set((data.users||[]).map(u => u.id)));
                            } else {
                              setSelectedUsers(new Set());
                            }
                          }} />
                      </th>
                      {['User','Email','Role','Status','Joined','Actions'].map(h => (
                        <th key={h} style={{ padding:'10px 14px', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:'1px', color:'var(--text-soft)', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.users||[]).map(u => (
                      <tr key={u.id} style={{ borderBottom:'1px solid var(--border)', opacity: u.is_banned ? 0.6 : 1, background: selectedUsers.has(u.id) ? 'rgba(200,100,0,0.1)' : 'transparent' }}>
                        <td style={{ padding:'12px 14px', width:40 }}>
                          <input type="checkbox"
                            checked={selectedUsers.has(u.id)}
                            onChange={e => {
                              const s = new Set(selectedUsers);
                              if (e.target.checked) s.add(u.id);
                              else s.delete(u.id);
                              setSelectedUsers(s);
                            }} />
                        </td>
                        <td style={{ padding:'12px 14px' }}>
                          <div style={{ fontWeight:600 }}>{u.first_name} {u.last_name}</div>
                          {u.id_verified && <div style={{ fontSize:11, color:'var(--green)' }}>✅ ID Verified</div>}
                        </td>
                        <td style={{ padding:'12px 14px', color:'var(--text-soft)', fontSize:12 }}>{u.email}</td>
                        <td style={{ padding:'12px 14px' }}>
                          <select
                            value={u.role}
                            disabled={u.role === 'superadmin'}
                            onChange={async e => {
                              const newRole = e.target.value;
                              if (!window.confirm(`Change ${u.first_name}'s role to ${newRole}?`)) return;
                              await API.patch(`/admin/users/${u.id}/role`, { role: newRole });
                              setData(p => ({ ...p, users: p.users.map(x => x.id===u.id ? {...x,role:newRole} : x) }));
                            }}
                            style={{ padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', fontSize:12, background: u.role==='admin'?'#FDECEA':'white', color: u.role==='admin'?'var(--cherry)':'inherit', cursor: u.role==='superadmin'?'not-allowed':'pointer' }}>
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            {u.role === 'superadmin' && <option value="superadmin">Superadmin</option>}
                          </select>
                        </td>
                        <td style={{ padding:'12px 14px' }}>
                          <span className={`pill ${u.is_banned?'pill-pending':'pill-active'}`}>
                            {u.is_banned ? '🚫 Banned' : '✅ Active'}
                          </span>
                          {u.is_banned && u.ban_reason && (
                            <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:3, maxWidth:140 }}>{u.ban_reason}</div>
                          )}
                        </td>
                        <td style={{ padding:'12px 14px', color:'var(--text-soft)', fontSize:12, whiteSpace:'nowrap' }}>
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding:'12px 14px' }}>
                          <div style={{ display:'flex', gap:6 }}>
                            {u.is_banned ? (
                              <button className="btn btn-green btn-sm" onClick={() => banUser(u.id, false)}>Unban</button>
                            ) : (
                              <button className="btn btn-sm" style={{ background:'#FDECEA', color:'var(--cherry)', border:'none' }}
                                onClick={() => {
                                  const reason = window.prompt(`Reason for banning ${u.first_name}?`);
                                  if (reason !== null) banUser(u.id, true, reason);
                                }}>
                                Ban
                              </button>
                            )}
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/profile/${u.id}`)}>View</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!data.users?.length && <p style={{ color:'var(--text-soft)', padding:20 }}>No users yet</p>}
            </div>
          )}

          {/* ── LISTINGS TAB ── */}
          {tab === 'listings' && (
            <div>
              {/* Filters */}
              <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
                <input className="form-input" placeholder="Search listings or seller email..."
                  style={{ flex:1, minWidth:200 }}
                  onChange={e => {
                    const q = e.target.value;
                    clearTimeout(window._listingSearch);
                    window._listingSearch = setTimeout(() => {
                      API.get(`/admin/listings?q=${encodeURIComponent(q)}`).then(r => setData(p => ({...p, listings: r.data.listings})));
                    }, 400);
                  }} />
                {['all','active','removed','archived','sold','rented'].map(s => (
                  <button key={s} className="btn btn-sm btn-ghost"
                    onClick={() => API.get(`/admin/listings${s!=='all'?`?status=${s}`:''}`).then(r => setData(p => ({...p, listings: r.data.listings})))}>
                    {s.charAt(0).toUpperCase()+s.slice(1)}
                  </button>
                ))}
              </div>

              {!data.listings?.length && <p style={{ color:'var(--text-soft)' }}>No listings found</p>}
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {(data.listings||[]).map(l => (
                  <div key={l.id} style={{ background:'white', border:`1px solid ${l.is_featured?'var(--cherry)':'var(--border)'}`, borderRadius:12, padding:'14px 18px', display:'flex', gap:14, alignItems:'center', flexWrap:'wrap', opacity: l.status==='removed'?0.6:1 }}>

                    {/* Thumbnail */}
                    <div style={{ width:48, height:48, borderRadius:8, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                      {l.primary_image
                        ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.primary_image}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                        : '📦'
                      }
                    </div>

                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>{l.title}</div>
                        {l.is_featured && <span style={{ fontSize:10, fontWeight:700, background:'var(--cherry)', color:'white', padding:'2px 6px', borderRadius:4 }}>⭐ FEATURED</span>}
                      </div>
                      <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                        {l.first_name} {l.last_name} · {l.email} · {l.category_label}
                        {l.flag_reason && <span style={{ color:'var(--cherry)', marginLeft:8 }}>⚠️ {l.flag_reason}</span>}
                      </div>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span className={`pill ${l.listing_type==='rent'?'pill-rent':l.listing_type==='auction'?'pill-auction':'pill-sale'}`}>
                        {l.listing_type}
                      </span>
                      <span className={`pill ${l.status==='active'?'pill-active':l.status==='removed'?'pill-pending':'pill-pending'}`}>
                        {l.status}
                      </span>
                    </div>

                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {/* Feature / unfeature */}
                      <button
                        className="btn btn-sm btn-ghost"
                        title={l.is_featured ? 'Remove from featured' : 'Feature this listing'}
                        onClick={() => featureListing(l.id, !l.is_featured)}
                        style={{ color: l.is_featured ? 'var(--cherry)' : 'var(--text-soft)' }}>
                        {l.is_featured ? '★ Unfeature' : '☆ Feature'}
                      </button>

                      {/* Edit */}
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${l.id}/edit`)}>✏️ Edit</button>

                      {/* View */}
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${l.slug || l.id}`)}>View</button>

                      {/* Remove / restore */}
                      {l.status === 'removed' ? (
                        <button className="btn btn-green btn-sm" onClick={() => restoreListing(l.id)}>↩ Restore</button>
                      ) : (
                        <button className="btn btn-sm" style={{ background:'#FDECEA', color:'var(--cherry)', border:'none' }} onClick={() => removeListing(l.id)}>
                          🚫 Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ORDERS TAB ── */}
          {tab === 'orders' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700 }}>
                  All Orders & Bookings ({data.orders?.length || 0})
                </h3>
              </div>
              {!data.orders?.length ? (
                <p style={{ color:'var(--text-soft)' }}>No orders yet</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {(data.orders || []).map(o => {
                    const isRental = o.record_type === 'booking';
                    const statusColors = {
                      pending:   { bg:'#FFF9E6', color:'#C9930A' },
                      confirmed: { bg:'#EAF5EC', color:'#2D7A3A' },
                      active:    { bg:'#EAF5EC', color:'#2D7A3A' },
                      shipped:   { bg:'#EBF0FF', color:'#1A5276' },
                      delivered: { bg:'#EBF0FF', color:'#1A5276' },
                      completed: { bg:'#EAF5EC', color:'#2D7A3A' },
                      cancelled: { bg:'#F7F7F7', color:'#888'    },
                    };
                    const sc = statusColors[o.status] || { bg:'#F7F7F7', color:'#888' };
                    return (
                      <div key={`${o.record_type}-${o.id}`} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px' }}>
                        <div style={{ display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
                          {/* Thumbnail */}
                          <div style={{ width:52, height:52, borderRadius:8, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                            {o.primary_image
                              ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${o.primary_image}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                              : (isRental ? '📅' : '📦')}
                          </div>
                          {/* Info */}
                          <div style={{ flex:1, minWidth:160 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:700, fontSize:14 }}>{o.title}</span>
                              <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:100,
                                background: isRental ? '#EBF0FF' : '#EAF5EC',
                                color:      isRental ? '#1A5276'  : '#2D7A3A' }}>
                                {isRental ? 'RENTAL' : 'SALE'}
                              </span>
                            </div>
                            <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                              Buyer: <strong>{o.buyer_first} {o.buyer_last}</strong> ({o.buyer_email})
                            </div>
                            <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                              Seller: <strong>{o.seller_first} {o.seller_last}</strong> ({o.seller_email})
                            </div>
                            {o.tracking_number && (
                              <div style={{ fontSize:12, color:'#1A5276', marginTop:2 }}>Tracking: {o.tracking_number}</div>
                            )}
                          </div>
                          {/* Amount */}
                          <div style={{ textAlign:'right', flexShrink:0 }}>
                            <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:16 }}>
                              ${parseFloat(o.total_charged||0).toFixed(2)}
                            </div>
                            <div style={{ fontSize:11, color:'var(--text-soft)', marginBottom:6 }}>
                              {o.created_at ? format(new Date(o.created_at),'MMM d, yyyy') : ''}
                            </div>
                            <span style={{ fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:100, background:sc.bg, color:sc.color }}>
                              {o.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── DISPUTES TAB ── */}
          {tab === 'disputes' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>⚠️ Disputes ({data.disputes?.length||0})</h3>
                <div style={{ display:'flex', gap:6 }}>
                  {['open','resolved','closed','all'].map(s => (
                    <button key={s} className="btn btn-sm btn-ghost" onClick={() => API.get(`/admin/disputes?status=${s}`).then(r => setData(p => ({...p, disputes: r.data.disputes})))} style={{ textTransform:'capitalize' }}>{s}</button>
                  ))}
                </div>
              </div>
              {!data.disputes?.length ? (
                <div style={{ textAlign:'center', padding:40, background:'var(--cream)', borderRadius:12 }}>
                  <div style={{ fontSize:40, marginBottom:8 }}>🎉</div>
                  <p style={{ color:'var(--text-soft)' }}>No open disputes</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {(data.disputes||[]).map(d => (
                    <AdminDisputeCard key={d.id} dispute={d} onUpdate={updated => setData(p => ({...p, disputes: p.disputes.map(x => x.id===updated.id ? updated : x)}))} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── SUGGESTIONS TAB ── */}
          {tab === 'suggestions' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {!data.suggestions?.length && <p style={{ color:'var(--text-soft)' }}>No pending suggestions</p>}
              {(data.suggestions||[]).map(s => (
                <div key={s.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', gap:14, alignItems:'center' }}>
                  <span style={{ fontSize:24 }}>{s.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700 }}>{s.label}</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>Suggested by {s.first_name} · {s.vote_count} vote{s.vote_count!==1?'s':''}</div>
                  </div>
                  <button className="btn btn-green btn-sm"
                    onClick={async () => {
                      await API.post(`/categories/${s.id}/promote`);
                      setData(p => ({ ...p, suggestions: p.suggestions.filter(x=>x.id!==s.id) }));
                    }}>
                    ✅ Promote
                  </button>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => setData(p => ({ ...p, suggestions: p.suggestions.filter(x=>x.id!==s.id) }))}>
                    Dismiss
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── REVIEWS TAB ── */}
          {tab === 'reviews' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>
                ⭐ Pending Review Moderation ({data.reviews?.length || 0})
              </h3>
              {!data.reviews?.length ? (
                <div style={{ background:'white', borderRadius:12, padding:40, textAlign:'center', color:'var(--text-soft)' }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
                  <div>All reviews are moderated!</div>
                </div>
              ) : (
                <div style={{ display:'grid', gap:16 }}>
                  {data.reviews.map(review => (
                    <div key={review.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:20 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start', marginBottom:16 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>
                            {review.reviewer_first} {review.reviewer_last} → {review.reviewee_first} {review.reviewee_last}
                          </div>
                          {review.listing_title && (
                            <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom:8 }}>
                              📍 Re: {review.listing_title}
                            </div>
                          )}
                          <div style={{ fontSize:14, marginBottom:12 }}>
                            {'⭐'.repeat(review.rating)}
                            <span style={{ marginLeft:8, fontWeight:600 }}>{review.rating}/5</span>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <button
                            onClick={async () => {
                              await API.patch(`/reviews/${review.id}/moderate`, { action: 'approve' });
                              setData(p => ({ ...p, reviews: p.reviews.filter(r => r.id !== review.id) }));
                            }}
                            style={{ padding:'8px 16px', background:'#4CAF50', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:12 }}>
                            ✅ Approve
                          </button>
                          <button
                            onClick={async () => {
                              await API.patch(`/reviews/${review.id}/moderate`, { action: 'reject' });
                              setData(p => ({ ...p, reviews: p.reviews.filter(r => r.id !== review.id) }));
                            }}
                            style={{ padding:'8px 16px', background:'#F44336', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:12 }}>
                            ✕ Reject
                          </button>
                        </div>
                      </div>

                      {review.title && (
                        <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>
                          {review.title}
                        </div>
                      )}

                      {review.body && (
                        <div style={{ fontSize:13, color:'var(--text-mid)', lineHeight:1.6, marginBottom:12 }}>
                          {review.body}
                        </div>
                      )}

                      {review.feedback_tags && review.feedback_tags.length > 0 && (
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                          {JSON.parse(typeof review.feedback_tags === 'string' ? review.feedback_tags : JSON.stringify(review.feedback_tags)).map(tag => {
                            const tagMap = {
                              'quick_responder': { icon:'⚡', label:'Quick responder' },
                              'item_as_described': { icon:'✅', label:'Item as described' },
                              'safe_to_meet': { icon:'🛡️', label:'Safe to meet' },
                              'fair_prices': { icon:'💰', label:'Fair prices' },
                            };
                            const t = tagMap[tag] || { icon:'🏷️', label: tag };
                            return (
                              <div key={tag} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px', background:'#E8F5E9', border:'1px solid #4CAF50', borderRadius:16, fontSize:11, fontWeight:600, color:'#2E7D32' }}>
                                {t.icon} {t.label}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div style={{ fontSize:11, color:'var(--text-soft)', paddingTop:12, borderTop:'1px solid var(--border)' }}>
                        Submitted {new Date(review.created_at).toLocaleDateString()} at {new Date(review.created_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PROMOTIONS TAB ── */}
          {tab === 'promotions' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>
                💰 Active Promotions ({data.promotions?.length || 0})
              </h3>
              {!data.promotions?.length ? (
                <div style={{ background:'white', borderRadius:12, padding:40, textAlign:'center', color:'var(--text-soft)' }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>📭</div>
                  <div>No active promotions</div>
                </div>
              ) : (
                <div>
                  <div style={{ overflowX:'auto', marginBottom:20 }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead>
                        <tr style={{ borderBottom:'2px solid var(--border)' }}>
                          <th style={{ padding:'12px 8px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Seller</th>
                          <th style={{ padding:'12px 8px', textAlign:'left', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Listing</th>
                          <th style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Tier</th>
                          <th style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Cost</th>
                          <th style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Views</th>
                          <th style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Clicks</th>
                          <th style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>Est. Revenue</th>
                          <th style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', fontSize:11 }}>ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.promotions.map(promo => {
                          const tierColors = { basic:'#E0E0E0', premium:'#C9930A', vip:'#FFD700' };
                          const tierLabels = { basic:'Basic', premium:'Premium', vip:'VIP' };
                          const roiColor = promo.roi >= 0 ? '#4CAF50' : '#F44336';
                          return (
                            <tr key={promo.id} style={{ borderBottom:'1px solid var(--border)', background:promo.hours_remaining > 0 ? 'white' : '#F5F5F5' }}>
                              <td style={{ padding:'12px 8px' }}>
                                <div style={{ fontWeight:600 }}>{promo.first_name} {promo.last_name}</div>
                                <div style={{ fontSize:11, color:'var(--text-soft)' }}>{promo.username || '-'}</div>
                              </td>
                              <td style={{ padding:'12px 8px' }}>
                                <div style={{ fontWeight:600, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {promo.title}
                                </div>
                              </td>
                              <td style={{ padding:'12px 8px', textAlign:'center' }}>
                                <div style={{ display:'inline-block', padding:'4px 12px', background:tierColors[promo.tier], color:'#1C1C1C', borderRadius:20, fontSize:11, fontWeight:700 }}>
                                  {tierLabels[promo.tier]}
                                </div>
                              </td>
                              <td style={{ padding:'12px 8px', textAlign:'center', fontWeight:700 }}>
                                ${promo.price}
                              </td>
                              <td style={{ padding:'12px 8px', textAlign:'center' }}>
                                {promo.impressions}
                              </td>
                              <td style={{ padding:'12px 8px', textAlign:'center' }}>
                                {promo.clicks}
                              </td>
                              <td style={{ padding:'12px 8px', textAlign:'center', fontWeight:700 }}>
                                ${promo.estimated_revenue || 0}
                              </td>
                              <td style={{ padding:'12px 8px', textAlign:'center', fontWeight:700, color:roiColor }}>
                                {promo.roi >= 0 ? '+' : ''}{promo.roi}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginTop:28 }}>
                    {[
                      { label:'Avg ROI', value:`${(data.promotions.reduce((s,p)=>s+p.roi,0)/data.promotions.length).toFixed(1)}%`, color:'#2D7A3A' },
                      { label:'Total Revenue', value:`$${data.promotions.reduce((s,p)=>s+p.estimated_revenue,0)}`, color:'#C0392B' },
                      { label:'Total Views', value:data.promotions.reduce((s,p)=>s+p.impressions,0), color:'#1A5276' }
                    ].map(stat => (
                      <div key={stat.label} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:20 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'var(--text-soft)', marginBottom:8, textTransform:'uppercase' }}>
                          {stat.label}
                        </div>
                        <div style={{ fontSize:26, fontWeight:700, color:stat.color }}>
                          {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ACTIVITY LOGS TAB ── */}
          {tab === 'activity-logs' && (
            <div>
              <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <h3 style={{ fontSize:16, fontWeight:700, marginBottom:12 }}>User Activity Logs</h3>
                  <p style={{ fontSize:13, color:'var(--text-soft)', marginBottom:0 }}>Track all user actions and admin changes</p>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={exportActivityLogs}>📥 Export CSV</button>
              </div>

              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:800 }}>
                  <thead>
                    <tr style={{ background:'var(--cream)', textAlign:'left' }}>
                      {['Time','User','Action','Resource','Details'].map(h => (
                        <th key={h} style={{ padding:'10px 14px', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:'1px', color:'var(--text-soft)', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.logs||[]).map(log => (
                      <tr key={log.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'10px 14px', fontSize:11, color:'var(--text-soft)', whiteSpace:'nowrap' }}>
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td style={{ padding:'10px 14px' }}>
                          <div style={{ fontWeight:600, fontSize:12 }}>{log.first_name} {log.last_name}</div>
                          <div style={{ fontSize:11, color:'var(--text-soft)' }}>{log.email}</div>
                        </td>
                        <td style={{ padding:'10px 14px' }}>
                          <span style={{ background:'var(--cherry-light)', color:'var(--cherry)', padding:'3px 8px', borderRadius:4, fontSize:11, fontWeight:600 }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ padding:'10px 14px', fontSize:11, color:'var(--text-soft)' }}>
                          {log.resource_type || '-'}
                        </td>
                        <td style={{ padding:'10px 14px', fontSize:11 }}>
                          {log.details ? (
                            <details style={{ cursor:'pointer' }}>
                              <summary style={{ fontWeight:500 }}>View</summary>
                              <pre style={{ fontSize:10, background:'var(--cream)', padding:8, borderRadius:4, marginTop:4, overflow:'auto' }}>
                                {JSON.stringify(JSON.parse(log.details), null, 2)}
                              </pre>
                            </details>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!data.logs?.length && <p style={{ color:'var(--text-soft)', padding:20, textAlign:'center' }}>No activity yet</p>}
            </div>
          )}

          {/* ── SCRIPTS TAB ── */}
          {tab === 'scripts' && <ScriptManager />}

          {/* ── CATEGORIES ── */}
          {tab === 'categories' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>🗂️ Category Management</h3>
              {/* Add / Edit form */}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:24 }}>
                <h4 style={{ fontWeight:700, marginBottom:14 }}>{catEdit ? '✏️ Edit Category' : '➕ Add Category'}</h4>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 60px 1fr 80px', gap:10, marginBottom:10 }}>
                  <input className="form-input" placeholder="Label (e.g. Electronics)" value={catEdit?.label ?? catForm.label} onChange={e => catEdit ? setCatEdit(p=>({...p,label:e.target.value})) : setCatForm(p=>({...p,label:e.target.value}))} />
                  <input className="form-input" placeholder="🔧" value={catEdit?.icon ?? catForm.icon} onChange={e => catEdit ? setCatEdit(p=>({...p,icon:e.target.value})) : setCatForm(p=>({...p,icon:e.target.value}))} />
                  <input className="form-input" placeholder="Slug (e.g. electronics)" value={catEdit?.slug ?? catForm.slug} onChange={e => catEdit ? setCatEdit(p=>({...p,slug:e.target.value})) : setCatForm(p=>({...p,slug:e.target.value}))} />
                  <input className="form-input" type="number" placeholder="Order" value={catEdit?.sort_order ?? catForm.sortOrder} onChange={e => catEdit ? setCatEdit(p=>({...p,sort_order:e.target.value})) : setCatForm(p=>({...p,sortOrder:e.target.value}))} />
                </div>
                <input className="form-input" placeholder="Description (optional)" style={{ marginBottom:10 }} value={catEdit?.description ?? catForm.description} onChange={e => catEdit ? setCatEdit(p=>({...p,description:e.target.value})) : setCatForm(p=>({...p,description:e.target.value}))} />
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-cherry btn-sm" onClick={async () => {
                    if (catEdit) {
                      await API.patch(`/admin/categories/${catEdit.id}`, { label:catEdit.label, icon:catEdit.icon, description:catEdit.description, sortOrder:catEdit.sort_order, status:catEdit.status });
                      setCatEdit(null);
                    } else {
                      await API.post('/admin/categories', catForm);
                      setCatForm({ label:'', icon:'📦', description:'', slug:'', sortOrder:0 });
                    }
                    fetchTab('categories');
                  }}>{catEdit ? '💾 Save' : '➕ Add Category'}</button>
                  {catEdit && <button className="btn btn-ghost btn-sm" onClick={() => setCatEdit(null)}>Cancel</button>}
                </div>
              </div>
              {/* Category list */}
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {(data.categories || []).map(c => (
                  <div key={c.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:12 }}>
                    <span style={{ fontSize:22 }}>{c.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700 }}>{c.label} <span style={{ fontSize:11, color:'var(--text-soft)' }}>/{c.slug}</span></div>
                      {c.description && <div style={{ fontSize:12, color:'var(--text-soft)' }}>{c.description}</div>}
                    </div>
                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:100, background: c.status==='active'?'#EAF5EC':'#F7F7F7', color: c.status==='active'?'#2D7A3A':'#888', fontWeight:700 }}>{c.status}</span>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setCatEdit({...c})}>✏️</button>
                      <button className="btn btn-ghost btn-sm" onClick={async () => {
                        const newStatus = c.status === 'active' ? 'hidden' : 'active';
                        await API.patch(`/admin/categories/${c.id}`, { status: newStatus });
                        fetchTab('categories');
                      }}>{c.status === 'active' ? '🙈 Hide' : '👁 Show'}</button>
                      <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={async () => {
                        if (!window.confirm(`Delete "${c.label}"?`)) return;
                        API.delete(`/admin/categories/${c.id}`).then(() => fetchTab('categories')).catch(e => alert(e.response?.data?.error || 'Cannot delete'));
                      }}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── PAYOUTS ── */}
          {tab === 'payouts' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>💸 Payout Management</h3>
                <AdminTriggerPayout onDone={() => fetchTab('payouts')} />
              </div>
              {!data.payouts?.length ? (
                <div style={{ textAlign:'center', padding:40, background:'var(--cream)', borderRadius:12 }}>
                  <p style={{ color:'var(--text-soft)' }}>No payouts yet</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {data.payouts.map(p => (
                    <div key={p.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:180 }}>
                        <div style={{ fontWeight:700 }}>{p.first_name} {p.last_name} <span style={{ fontWeight:400, color:'var(--text-soft)', fontSize:12 }}>{p.email}</span></div>
                        <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                          {new Date(p.created_at).toLocaleDateString()}{p.paid_at ? ` · Paid ${new Date(p.paid_at).toLocaleDateString()}` : ''}
                          {p.stripe_account_id && <span style={{ color:'#635BFF', marginLeft:6 }}>Stripe ✓</span>}
                        </div>
                      </div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:18 }}>${parseFloat(p.amount).toFixed(2)}</div>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background:p.status==='completed'?'#EAF5EC':p.status==='processing'?'#EBF0FF':'#FFF9E6', color:p.status==='completed'?'#2D7A3A':p.status==='processing'?'#1A5276':'#C9930A' }}>{p.status}</span>
                      {p.status !== 'completed' && (
                        <button className="btn btn-cherry btn-sm" onClick={() => API.patch(`/admin/payouts/${p.id}`, { status:'completed' }).then(() => fetchTab('payouts'))}>✅ Mark Paid</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── RETURNS ── */}
          {tab === 'returns' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>↩️ Return Requests</h3>
                <div style={{ display:'flex', gap:8 }}>
                  {['pending','approved','rejected','all'].map(s => (
                    <button key={s} className={`btn btn-sm btn-ghost`} onClick={() => API.get(`/admin/returns?status=${s}`).then(r => setData(p => ({ ...p, returns: r.data.returns })))} style={{ textTransform:'capitalize' }}>{s}</button>
                  ))}
                </div>
              </div>
              {!data.returns?.length ? (
                <div style={{ textAlign:'center', padding:40, background:'var(--cream)', borderRadius:12 }}>
                  <p style={{ color:'var(--text-soft)' }}>No return requests</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {data.returns.map(r => (
                    <AdminReturnCard key={r.id} ret={r} onUpdate={(updated) => setData(p => ({ ...p, returns: p.returns.map(x => x.id===updated.id ? updated : x) }))} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── REPORTS ── */}
          {tab === 'reports' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>📊 Revenue Reports</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  window.open(`${process.env.REACT_APP_API_URL}/admin/reports/revenue?format=csv`, '_blank');
                }}>📥 Export CSV</button>
              </div>
              {data.totals && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
                  {[['Transactions', data.totals.count, '📦','#1A5276'],['Gross Revenue',`$${parseFloat(data.totals.gross||0).toFixed(2)}`, '💵','#2D7A3A'],['Platform Fees',`$${parseFloat(data.totals.fees||0).toFixed(2)}`,'💰','#C0392B'],['Net to Sellers',`$${parseFloat(data.totals.net||0).toFixed(2)}`,'💸','#8E44AD']].map(([l,v,i,c]) => (
                    <div key={l} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px' }}>
                      <div style={{ fontSize:20 }}>{i}</div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:20, color:c }}>{v}</div>
                      <div style={{ fontSize:11, color:'var(--text-soft)' }}>{l}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead><tr style={{ background:'var(--cream)' }}>{['Date','Type','Gross','Fee','Net','Payer','Payee'].map(h=><th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:700, fontSize:12, color:'var(--text-soft)' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data.transactions||[]).slice(0,50).map(t => (
                      <tr key={t.id} style={{ borderTop:'1px solid var(--border)' }}>
                        <td style={{ padding:'8px 12px' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                        <td style={{ padding:'8px 12px' }}>{t.reference_type}</td>
                        <td style={{ padding:'8px 12px', fontWeight:700 }}>${parseFloat(t.gross_amount||0).toFixed(2)}</td>
                        <td style={{ padding:'8px 12px', color:'var(--cherry)' }}>${parseFloat(t.platform_fee||0).toFixed(2)}</td>
                        <td style={{ padding:'8px 12px', color:'#2D7A3A' }}>${parseFloat(t.net_amount||0).toFixed(2)}</td>
                        <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{t.payer_email||'-'}</td>
                        <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{t.payee_email||'-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ID VERIFICATIONS ── */}
          {tab === 'verifications' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>🪪 ID Verification Queue</h3>
              {!data.verifications?.length ? (
                <div style={{ textAlign:'center', padding:40, background:'var(--cream)', borderRadius:12 }}>
                  <p style={{ color:'var(--text-soft)' }}>No pending verifications</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {data.verifications.map(v => (
                    <div key={v.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'18px 20px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:15 }}>{v.first_name} {v.last_name}</div>
                          <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                            {v.email} · <strong>{v.doc_type?.replace(/_/g,' ')}</strong> · Submitted {new Date(v.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, fontWeight:700,
                          background: v.status==='pending'?'#FFF8E1': v.status==='approved'?'#EAF5EC':'#FFEBEE',
                          color: v.status==='pending'?'#E65100': v.status==='approved'?'#2D7A3A':'#C62828' }}>
                          {v.status}
                        </span>
                      </div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
                        {v.doc_front_url && <a href={`${API_BASE}${v.doc_front_url}`} target="_blank" rel="noreferrer"><img src={`${API_BASE}${v.doc_front_url}`} alt="Front" style={{ height:90, borderRadius:8, objectFit:'cover', cursor:'pointer', border:'1px solid var(--border)' }} /></a>}
                        {v.doc_back_url && <a href={`${API_BASE}${v.doc_back_url}`} target="_blank" rel="noreferrer"><img src={`${API_BASE}${v.doc_back_url}`} alt="Back" style={{ height:90, borderRadius:8, objectFit:'cover', cursor:'pointer', border:'1px solid var(--border)' }} /></a>}
                        {v.selfie_url && <a href={`${API_BASE}${v.selfie_url}`} target="_blank" rel="noreferrer"><img src={`${API_BASE}${v.selfie_url}`} alt="Selfie" style={{ height:90, borderRadius:8, objectFit:'cover', cursor:'pointer', border:'1px solid var(--border)' }} /></a>}
                      </div>
                      {rejectingId === v.id ? (
                        <div>
                          <textarea
                            className="form-input"
                            placeholder="Rejection reason (sent to user)…"
                            rows={2}
                            value={rejectNotes}
                            onChange={e => setRejectNotes(e.target.value)}
                            style={{ marginBottom:8, resize:'vertical' }}
                          />
                          <div style={{ display:'flex', gap:8 }}>
                            <button className="btn btn-cherry btn-sm" onClick={() => {
                              API.patch(`/admin/verifications/${v.id}`, { action:'reject', notes:rejectNotes })
                                .then(() => { setRejectingId(null); setRejectNotes(''); fetchTab('verifications'); });
                            }}>Confirm Reject</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => { setRejectingId(null); setRejectNotes(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display:'flex', gap:8 }}>
                          <button className="btn btn-cherry btn-sm" onClick={() => { API.patch(`/admin/verifications/${v.id}`, { action:'approve' }).then(() => fetchTab('verifications')); }}>✅ Approve</button>
                          <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={() => { setRejectingId(v.id); setRejectNotes(''); }}>❌ Reject</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── IMAGE MODERATION ── */}
          {tab === 'moderation' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>🖼️ Flagged Images</h3>
              {!data.flags?.length ? (
                <div style={{ textAlign:'center', padding:40, background:'var(--cream)', borderRadius:12 }}>
                  <p style={{ color:'var(--text-soft)' }}>No flagged images</p>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:16 }}>
                  {data.flags.map(f => (
                    <div key={f.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                      <img src={f.image_url} alt="" style={{ width:'100%', height:160, objectFit:'cover' }} />
                      <div style={{ padding:'12px 14px' }}>
                        <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>{f.listing_title}</div>
                        <div style={{ fontSize:11, color:'var(--text-soft)', marginBottom:10 }}>{f.seller_email} · {f.reason}</div>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => API.patch(`/admin/images/flags/${f.id}`, { action:'approve' }).then(() => fetchTab('moderation'))}>✅ Keep</button>
                          <button className="btn btn-sm" style={{ background:'var(--cherry)', color:'white', border:'none', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:12 }} onClick={() => API.patch(`/admin/images/flags/${f.id}`, { action:'remove' }).then(() => fetchTab('moderation'))}>🗑️ Remove</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PROHIBITED KEYWORDS ── */}
          {tab === 'keywords' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>🚫 Prohibited Keywords</h3>
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:20 }}>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  <input className="form-input" placeholder="Keyword (e.g. weapon)" value={kwForm.keyword} onChange={e=>setKwForm(p=>({...p,keyword:e.target.value}))} style={{ flex:1, minWidth:180 }} />
                  <select className="form-input" value={kwForm.action} onChange={e=>setKwForm(p=>({...p,action:e.target.value}))} style={{ width:120 }}>
                    <option value="flag">Flag</option>
                    <option value="block">Block</option>
                  </select>
                  <button className="btn btn-cherry btn-sm" onClick={() => { API.post('/admin/keywords', kwForm).then(() => { setKwForm({keyword:'',action:'flag'}); fetchTab('keywords'); }); }}>Add</button>
                </div>
                <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:8 }}>Flag = listing gets flagged for review. Block = listing is rejected outright.</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {(data.keywords||[]).map(k => (
                  <div key={k.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div><span style={{ fontWeight:700 }}>{k.keyword}</span> <span style={{ fontSize:11, padding:'1px 6px', borderRadius:100, background: k.action==='block'?'#FDECEA':'#FFF9E6', color: k.action==='block'?'var(--cherry)':'#C9930A', marginLeft:8 }}>{k.action}</span></div>
                    <button style={{ background:'none', border:'none', cursor:'pointer', color:'var(--cherry)', fontSize:18 }} onClick={() => API.delete(`/admin/keywords/${k.id}`).then(() => fetchTab('keywords'))}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── SEARCH ANALYTICS ── */}
          {tab === 'search-analytics' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>🔍 Search Analytics — Last 30 Days</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                <div>
                  <h4 style={{ fontWeight:700, marginBottom:12 }}>🔥 Top Searches</h4>
                  <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                    {(data.topSearches||[]).map((s,i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                        <span>{s.query}</span>
                        <span style={{ fontWeight:700, color:'var(--cherry)' }}>{s.count}x</span>
                      </div>
                    ))}
                    {!data.topSearches?.length && <div style={{ padding:20, color:'var(--text-soft)', textAlign:'center' }}>No data yet</div>}
                  </div>
                </div>
                <div>
                  <h4 style={{ fontWeight:700, marginBottom:12 }}>❌ Zero Results</h4>
                  <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                    {(data.zeroResults||[]).map((s,i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                        <span>{s.query}</span>
                        <span style={{ fontWeight:700, color:'#C9930A' }}>{s.count}x</span>
                      </div>
                    ))}
                    {!data.zeroResults?.length && <div style={{ padding:20, color:'var(--text-soft)', textAlign:'center' }}>No zero-result searches</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── BACKUP ── */}
          {tab === 'backup' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>💾 Database Backups</h3>
                <button className="btn btn-cherry" onClick={() => {
                  API.post('/admin/backup/trigger').then(r => { alert('Backup completed!\n' + (r.data.output||'')); fetchTab('backup'); }).catch(err => alert('Backup failed: ' + (err.response?.data?.error||err.message)));
                }}>🔄 Run Backup Now</button>
              </div>
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                {(data.backups||[]).length === 0 ? (
                  <div style={{ padding:30, textAlign:'center', color:'var(--text-soft)' }}>No backups found</div>
                ) : (data.backups||[]).map((b,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                    <span style={{ fontFamily:'monospace' }}>{b.name}</span>
                    <span style={{ color:'var(--text-soft)' }}>{b.size} · {b.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── EMAILS ── */}
          {tab === 'emails' && (
            <div>
              <div style={{ marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:16 }}>📧 Email Log</h3>
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'16px 20px', marginBottom:16, display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
                  <div className="form-group" style={{ margin:0, flex:1, minWidth:180 }}>
                    <label className="form-label" style={{ marginBottom:4 }}>Send test to</label>
                    <input className="form-input" type="email" placeholder="test@example.com" value={emailTestTo} onChange={e => setEmailTestTo(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label" style={{ marginBottom:4 }}>Template</label>
                    <select className="form-input" value={emailTestTemplate} onChange={e => setEmailTestTemplate(e.target.value)}>
                      <option value="welcome">welcome</option>
                      <option value="reset_password">reset_password</option>
                      <option value="order_confirmed">order_confirmed</option>
                      <option value="booking_confirmed">booking_confirmed</option>
                    </select>
                  </div>
                  <button className="btn btn-ghost btn-sm" disabled={!emailTestTo || emailTestSending} onClick={async () => {
                    setEmailTestSending(true); setEmailTestMsg('');
                    try {
                      await API.post('/admin/emails/test', { to: emailTestTo, template: emailTestTemplate });
                      setEmailTestMsg('✅ Test email sent!');
                    } catch (err) { setEmailTestMsg('❌ ' + (err.response?.data?.error || 'Failed')); }
                    setEmailTestSending(false);
                    setTimeout(() => setEmailTestMsg(''), 4000);
                  }}>
                    {emailTestSending ? '⏳' : '📤 Send Test'}
                  </button>
                  {emailTestMsg && <div style={{ fontSize:13, fontWeight:600, color: emailTestMsg.startsWith('✅') ? '#2D7A3A' : 'var(--cherry)' }}>{emailTestMsg}</div>}
                </div>
              </div>
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead><tr style={{ background:'var(--cream)' }}>{['Date','To','Subject','Template','Status'].map(h=><th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:700, fontSize:12, color:'var(--text-soft)' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data.emails||[]).map(e => (
                      <tr key={e.id} style={{ borderTop:'1px solid var(--border)' }}>
                        <td style={{ padding:'8px 12px' }}>{new Date(e.created_at).toLocaleDateString()}</td>
                        <td style={{ padding:'8px 12px' }}>{e.to_email}</td>
                        <td style={{ padding:'8px 12px' }}>{e.subject}</td>
                        <td style={{ padding:'8px 12px', color:'var(--text-soft)' }}>{e.template}</td>
                        <td style={{ padding:'8px 12px' }}><span style={{ fontSize:11, fontWeight:700, padding:'2px 6px', borderRadius:100, background:e.status==='sent'?'#EAF5EC':'#FDECEA', color:e.status==='sent'?'#2D7A3A':'var(--cherry)' }}>{e.status}</span></td>
                      </tr>
                    ))}
                    {!data.emails?.length && <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:'var(--text-soft)' }}>No emails logged yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── SETTINGS (maintenance, announcements, fees) ── */}
          {tab === 'settings' && (
            <div style={{ maxWidth:700 }}>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:24 }}>⚙️ Site Settings</h3>

              {settingsSaved && <div className="alert alert-success" style={{ marginBottom:16 }}>{settingsSaved}</div>}

              {/* Maintenance Mode */}
              <div style={{ background: settingsForm.maintenance_mode==='true' ? '#FFF9E6' : 'white', border:`1px solid ${settingsForm.maintenance_mode==='true'?'#F5CBA7':'var(--border)'}`, borderRadius:14, padding:20, marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:16 }}>🔧 Maintenance Mode</div>
                    <div style={{ fontSize:13, color:'var(--text-soft)' }}>Takes the site offline for all non-admin users</div>
                  </div>
                  <button className={`btn ${settingsForm.maintenance_mode==='true'?'btn-cherry':'btn-ghost'}`} onClick={async () => {
                    const newVal = settingsForm.maintenance_mode==='true' ? 'false' : 'true';
                    setSettingsForm(p => ({...p, maintenance_mode: newVal}));
                    await API.patch('/admin/settings', { settings: { maintenance_mode: newVal } });
                    setSettingsSaved(newVal==='true' ? '🔧 Maintenance mode ON' : '✅ Maintenance mode OFF');
                    setTimeout(() => setSettingsSaved(''), 3000);
                  }}>
                    {settingsForm.maintenance_mode==='true' ? '✅ ON — Click to Disable' : 'Enable'}
                  </button>
                </div>
                <input className="form-input" placeholder="Maintenance message shown to users..." value={settingsForm.maintenance_message||''} onChange={e => setSettingsForm(p=>({...p,maintenance_message:e.target.value}))} />
              </div>

              {/* Announcement Banner */}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>📢 Announcement Banner</div>
                <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:12 }}>Shown as a banner at the top of every page</div>
                <input className="form-input" placeholder="Announcement text (leave blank to hide)" value={settingsForm.announcement||''} onChange={e => setSettingsForm(p=>({...p,announcement:e.target.value}))} style={{ marginBottom:8 }} />
                <select className="form-input" value={settingsForm.announcement_type||'info'} onChange={e => setSettingsForm(p=>({...p,announcement_type:e.target.value}))}>
                  <option value="info">ℹ️ Info (blue)</option>
                  <option value="success">✅ Success (green)</option>
                  <option value="warning">⚠️ Warning (yellow)</option>
                  <option value="error">❌ Alert (red)</option>
                </select>
              </div>

              {/* Platform Fees */}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>💰 Platform Fees</div>
                <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:12 }}>These override the hardcoded defaults</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {[['platform_fee_percent','Platform Fee %','10'],['subscription_price','Monthly Subscription ($)','25'],['barter_listing_fee','Barter Listing Fee ($)','10'],['barter_proposal_fee','Barter Proposal Fee ($)','10']].map(([key,label,def]) => (
                    <div key={key} className="form-group">
                      <label className="form-label">{label}</label>
                      <input className="form-input" type="number" step="0.01" placeholder={def} value={settingsForm[key]||''} onChange={e => setSettingsForm(p=>({...p,[key]:e.target.value}))} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Promotion Tier Pricing */}
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:24 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>⭐ Promotion Tier Pricing</div>
                <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:12 }}>Set prices for listing promotion tiers</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                  {[['promo_basic_price','Basic Price ($)','5'],['promo_premium_price','Premium Price ($)','10'],['promo_vip_price','VIP Price ($)','20']].map(([key,label,def]) => (
                    <div key={key} className="form-group">
                      <label className="form-label">{label}</label>
                      <input className="form-input" type="number" step="0.01" placeholder={def} value={settingsForm[key]||''} onChange={e => setSettingsForm(p=>({...p,[key]:e.target.value}))} />
                    </div>
                  ))}
                </div>
              </div>

              <button className="btn btn-cherry" onClick={async () => {
                await API.patch('/admin/settings', { settings: settingsForm });
                setSettingsSaved('✅ Settings saved');
                setTimeout(() => setSettingsSaved(''), 3000);
              }}>💾 Save All Settings</button>
            </div>
          )}

        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  OrdersPage — Buyer purchases + Seller sales
// ══════════════════════════════════════════════════════════
export function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('purchased'); // 'purchased' or 'sold'
  const [loading, setLoading] = useState(true);

  // Shipping label state
  const [shippingOrderId, setShippingOrderId] = useState(null);
  const [shippingForm, setShippingForm] = useState({ weightLbs:'1', lengthIn:'10', widthIn:'8', heightIn:'4' });
  const [shippingRates, setShippingRates] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingBuying, setShippingBuying] = useState(null);
  const [shippingResult, setShippingResult] = useState({});
  const [shippingError, setShippingError] = useState(null);

  // Batch label state
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { done, total, results }

  const toggleSelect = (id) => setSelectedOrders(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectAll = () => {
    const eligible = orders.filter(o => ['pending','confirmed'].includes(o.status));
    setSelectedOrders(new Set(eligible.map(o => o.id)));
  };

  const runBatchLabels = async () => {
    const eligible = orders.filter(o => selectedOrders.has(o.id) && ['pending','confirmed'].includes(o.status));
    if (!eligible.length) return;
    setBatchRunning(true);
    setBatchProgress({ done: 0, total: eligible.length, results: [] });

    for (const order of eligible) {
      try {
        // Get rates
        const ratesRes = await API.post('/shipping/rates', {
          fromName: (user.firstName || '') + ' ' + (user.lastName || ''),
          fromZip: '90001', fromCity: 'Los Angeles', fromState: 'CA',
          toName: order.buyer_first || 'Buyer',
          toZip: order.ship_to_zip || '10001', toCity: order.ship_to_city || 'New York', toState: order.ship_to_state || 'NY',
          weightLbs: order.weight_lbs || '1', lengthIn: order.length_in || '10',
          widthIn: order.width_in || '8', heightIn: order.height_in || '4',
        });
        // Pick cheapest rate
        const cheapest = ratesRes.data.rates?.[0];
        if (!cheapest) throw new Error('No rates available');

        const labelRes = await API.post('/shipping/label', { rateId: cheapest.id, orderId: order.id });
        setShippingResult(r => ({ ...r, [order.id]: labelRes.data }));
        setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'shipped', tracking_number: labelRes.data.trackingNumber } : o));
        setBatchProgress(p => ({ ...p, done: p.done + 1, results: [...p.results, { id: order.id, title: order.title, ok: true, labelUrl: labelRes.data.labelUrl, tracking: labelRes.data.trackingNumber }] }));
      } catch (err) {
        setBatchProgress(p => ({ ...p, done: p.done + 1, results: [...p.results, { id: order.id, title: order.title, ok: false, error: err.response?.data?.error || err.message }] }));
      }
    }
    setBatchRunning(false);
    setSelectedOrders(new Set());
  };

  const fetchRates = async (order) => {
    setShippingLoading(true); setShippingRates([]); setShippingError(null);
    try {
      const { data } = await API.post('/shipping/rates', {
        fromName: (user.firstName || '') + ' ' + (user.lastName || ''),
        fromZip: '90001', fromCity: 'Los Angeles', fromState: 'CA',
        toName: order.buyer_first || 'Buyer',
        toZip: order.ship_to_zip || '10001', toCity: order.ship_to_city || 'New York', toState: order.ship_to_state || 'NY',
        ...shippingForm,
      });
      setShippingRates(data.rates || []);
    } catch (err) { setShippingError(err.response?.data?.error || 'Failed to get rates'); }
    setShippingLoading(false);
  };

  const buyLabel = async (order, rateId) => {
    setShippingBuying(rateId);
    try {
      const { data } = await API.post('/shipping/label', { rateId, orderId: order.id });
      setShippingResult(r => ({ ...r, [order.id]: data }));
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'shipped', tracking_number: data.trackingNumber } : o));
      setShippingOrderId(null); setShippingRates([]);
    } catch (err) { setShippingError(err.response?.data?.error || 'Failed to buy label'); }
    setShippingBuying(null);
  };

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchOrders();
  }, [user, tab]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const endpoint = tab === 'purchased'
        ? '/orders?buyer=me'
        : '/orders?seller=me';
      const { data } = await API.get(endpoint);
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA500',
      confirmed: '#2196F3',
      awaiting_shipment: '#FF9800',
      shipped: '#4CAF50',
      delivered: '#2196F3',
      completed: '#4CAF50',
      disputed: '#F44336',
      refunded: '#9C27B0',
      cancelled: '#999'
    };
    return colors[status] || '#666';
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
          {tab === 'purchased' ? '📦 My Purchases' : '💰 My Sales'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>
          {tab === 'purchased'
            ? 'Track orders you\'ve placed'
            : 'Track items you\'ve sold'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
        {[
          { id: 'purchased', label: '🛍️ Purchased', icon: '📦' },
          { id: 'sold', label: '💵 Sold', icon: '💰' }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '14px 20px',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${tab === t.id ? 'var(--cherry)' : 'transparent'}`,
              color: tab === t.id ? 'var(--cherry)' : 'var(--text-soft)',
              fontWeight: tab === t.id ? 700 : 500,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Batch label toolbar — sold tab only */}
      {tab === 'sold' && orders.some(o => ['pending','confirmed'].includes(o.status)) && (
        <div style={{ background: '#F0F4F8', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
            onClick={selectedOrders.size > 0 ? () => setSelectedOrders(new Set()) : selectAll}>
            {selectedOrders.size > 0 ? `✕ Deselect (${selectedOrders.size})` : 'Select all pending'}
          </button>
          {selectedOrders.size > 0 && (
            <button style={{ background: '#1A5276', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              disabled={batchRunning} onClick={runBatchLabels}>
              {batchRunning ? `⏳ Generating… ${batchProgress?.done}/${batchProgress?.total}` : `📦 Generate ${selectedOrders.size} Label${selectedOrders.size > 1 ? 's' : ''} (cheapest rate)`}
            </button>
          )}
          {batchProgress && !batchRunning && (
            <div style={{ fontSize: 13, color: '#2D7A3A', fontWeight: 600 }}>
              ✅ {batchProgress.results.filter(r => r.ok).length} label{batchProgress.results.filter(r => r.ok).length !== 1 ? 's' : ''} generated
              {batchProgress.results.filter(r => !r.ok).length > 0 && ` · ${batchProgress.results.filter(r => !r.ok).length} failed`}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-soft)' }}>
          ⏳ Loading orders...
        </div>
      ) : orders.length === 0 ? (
        <div style={{ background: 'var(--cream)', borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>
            {tab === 'purchased' ? '📭' : '💼'}
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
            {tab === 'purchased' ? 'No purchases yet' : 'No sales yet'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 20 }}>
            {tab === 'purchased'
              ? 'Start shopping to see your orders here'
              : 'Your sold items will appear here'}
          </div>
          <button className="btn btn-cherry" onClick={() => navigate('/browse')}>
            Browse Items
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {orders.map(order => (
            <div
              key={order.id}
              style={{ position: 'relative', background: 'white', border: `1px solid ${selectedOrders.has(order.id) ? 'var(--cherry)' : 'var(--border)'}`, borderRadius: 14, padding: 20, cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => {
                if (!selectedOrders.has(order.id)) e.currentTarget.style.borderColor = 'var(--cherry)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(192,57,43,0.1)';
              }}
              onMouseLeave={e => {
                if (!selectedOrders.has(order.id)) e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => navigate(`/orders/${order.id}`)}
            >
              {/* Batch checkbox */}
              {tab === 'sold' && ['pending','confirmed'].includes(order.status) && (
                <div style={{ position: 'absolute', top: 14, right: 14 }} onClick={e => { e.stopPropagation(); toggleSelect(order.id); }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${selectedOrders.has(order.id) ? 'var(--cherry)' : 'var(--border)'}`, background: selectedOrders.has(order.id) ? 'var(--cherry)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    {selectedOrders.has(order.id) && <span style={{ color: 'white', fontSize: 13, fontWeight: 700 }}>✓</span>}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: 20, alignItems: 'start' }}>
                {/* Image */}
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: 10,
                  background: 'var(--cream)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {order.primary_image ? (
                    <img src={`${API_BASE}${order.primary_image}`} alt={order.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    '📦'
                  )}
                </div>

                {/* Details */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{order.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 8 }}>
                    Order #{order.order_number}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                    {tab === 'purchased'
                      ? `From ${order.seller_first} ${order.seller_last}`
                      : `To ${order.buyer_first || order.guest_name || 'Guest'}`}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4 }}>
                    {format(new Date(order.created_at), 'MMM d, yyyy')}
                  </div>
                  {order.tracking_number && (
                    <div style={{ fontSize: 12, color: '#1A5276', marginTop: 4 }}>📦 Tracking: {order.tracking_number}</div>
                  )}
                </div>

                {/* Status & Price */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    display: 'inline-block',
                    background: getStatusColor(order.status) + '20',
                    color: getStatusColor(order.status),
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    marginBottom: 12
                  }}>
                    {order.status.replace('_', ' ')}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    ${parseFloat(order.total_charged).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Buyer: Track Package button */}
              {tab === 'purchased' && order.tracking_number && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}
                  onClick={e => e.stopPropagation()}>
                  <button style={{ background: '#1A5276', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    onClick={() => navigate(`/track?order=${order.order_number}&email=${encodeURIComponent(user.email)}`)}>
                    📦 Track Package
                  </button>
                </div>
              )}

              {/* Seller actions */}
              {tab === 'sold' && ['pending','confirmed'].includes(order.status) && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}
                  onClick={e => e.stopPropagation()}>
                  {shippingResult[order.id] ? (
                    <a href={shippingResult[order.id].labelUrl} target="_blank" rel="noreferrer"
                      style={{ background: '#2D7A3A', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, textDecoration: 'none', display: 'inline-block' }}>
                      ⬇️ Download Label
                    </a>
                  ) : (
                    <button style={{ background: '#1A5276', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => {
                        if (shippingOrderId === order.id) { setShippingOrderId(null); return; }
                        setShippingOrderId(order.id);
                        setShippingRates([]); setShippingError(null);
                        setShippingForm({
                          weightLbs: order.weight_lbs ? String(order.weight_lbs) : '1',
                          lengthIn:  order.length_in  ? String(order.length_in)  : '10',
                          widthIn:   order.width_in   ? String(order.width_in)   : '8',
                          heightIn:  order.height_in  ? String(order.height_in)  : '4',
                        });
                      }}>
                      📦 {shippingOrderId === order.id ? 'Cancel' : 'Generate Label'}
                      {(order.weight_lbs || order.length_in) && shippingOrderId !== order.id && (
                        <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.25)', borderRadius: 4, padding: '1px 5px', marginLeft: 6 }}>preset</span>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Shipping panel */}
              {tab === 'sold' && shippingOrderId === order.id && (
                <div style={{ marginTop: 14, background: '#F0F4F8', borderRadius: 10, padding: 16 }}
                  onClick={e => e.stopPropagation()}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>📦 Package Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    {[['weightLbs','Weight (lbs)'],['lengthIn','Length (in)'],['widthIn','Width (in)'],['heightIn','Height (in)']].map(([k, lbl]) => (
                      <div key={k}>
                        <label style={{ fontSize: 11, color: 'var(--text-soft)', display: 'block', marginBottom: 3 }}>{lbl}</label>
                        <input className="form-input" type="number" min="0.1" step="0.1" style={{ fontSize: 13 }}
                          value={shippingForm[k]} onChange={e => setShippingForm(f => ({ ...f, [k]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-cherry btn-sm" disabled={shippingLoading} onClick={() => fetchRates(order)}>
                    {shippingLoading ? '⏳ Getting rates…' : '🔍 Get Rates'}
                  </button>
                  {shippingError && <div style={{ color: 'var(--cherry)', fontSize: 13, marginTop: 8 }}>{shippingError}</div>}
                  {shippingRates.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Select a rate:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {shippingRates.map(r => (
                          <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: 13 }}>{r.carrier}</span>
                              <span style={{ fontSize: 12, color: 'var(--text-soft)', marginLeft: 8 }}>{r.service}</span>
                              {r.days && <span style={{ fontSize: 11, color: 'var(--text-soft)', marginLeft: 6 }}>· {r.days} day{r.days !== 1 ? 's' : ''}</span>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontWeight: 700, color: 'var(--cherry)' }}>${r.rate.toFixed(2)}</span>
                              <button style={{ background: '#1A5276', color: 'white', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                disabled={shippingBuying === r.id}
                                onClick={() => buyLabel(order, r.id)}>
                                {shippingBuying === r.id ? '⏳…' : 'Buy'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  BookingsPage — Renter bookings + Owner rentals
// ══════════════════════════════════════════════════════════
export function BookingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('my_bookings'); // 'my_bookings' or 'rented_to_others'
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchBookings();
  }, [user, tab]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const endpoint = tab === 'my_bookings'
        ? '/bookings?renter=me'
        : '/bookings?owner=me';
      const { data } = await API.get(endpoint);
      setBookings(data.bookings || []);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA500',
      confirmed: '#2196F3',
      active: '#4CAF50',
      completed: '#4CAF50',
      cancelled: '#999'
    };
    return colors[status] || '#666';
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
          {tab === 'my_bookings' ? '📅 My Bookings' : '🏠 Rentals from Others'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>
          {tab === 'my_bookings'
            ? 'Track rental bookings you\'ve made'
            : 'Track rental items you\'ve listed'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
        {[
          { id: 'my_bookings', label: '📅 My Bookings' },
          { id: 'rented_to_others', label: '🏠 My Rentals' }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '14px 20px',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${tab === t.id ? 'var(--cherry)' : 'transparent'}`,
              color: tab === t.id ? 'var(--cherry)' : 'var(--text-soft)',
              fontWeight: tab === t.id ? 700 : 500,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-soft)' }}>
          ⏳ Loading bookings...
        </div>
      ) : bookings.length === 0 ? (
        <div style={{ background: 'var(--cream)', borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>
            {tab === 'my_bookings' ? '📭' : '🏠'}
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
            {tab === 'my_bookings' ? 'No bookings yet' : 'No active rentals'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 20 }}>
            {tab === 'my_bookings'
              ? 'Start booking rental items to see them here'
              : 'Your rental listings will appear here'}
          </div>
          <button className="btn btn-cherry" onClick={() => navigate('/browse')}>
            Browse Items
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {bookings.map(booking => (
            <div
              key={booking.id}
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: 20,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--cherry)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(192,57,43,0.1)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => navigate(`/rentals/${booking.id}`)}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: 20, alignItems: 'start' }}>
                {/* Image */}
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: 10,
                  background: 'var(--cream)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {booking.primary_image ? (
                    <img src={`${API_BASE}${booking.primary_image}`} alt={booking.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    '📅'
                  )}
                </div>

                {/* Details */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{booking.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 8 }}>
                    {tab === 'my_bookings'
                      ? `From ${booking.owner_first} ${booking.owner_last}`
                      : `Rented to ${booking.renter_first || 'Guest'}`}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>
                    {format(new Date(booking.start_date), 'MMM d')} - {format(new Date(booking.end_date), 'MMM d, yyyy')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                    {booking.days} day{booking.days !== 1 ? 's' : ''}
                  </div>
                </div>

                {/* Status & Price */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    display: 'inline-block',
                    background: getStatusColor(booking.status) + '20',
                    color: getStatusColor(booking.status),
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    marginBottom: 12
                  }}>
                    {booking.status}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    ${parseFloat(booking.total_charged).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  RatingsPage - User Ratings & Reviews
// ══════════════════════════════════════════════════════════
export function RatingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setLoading(false);
  }, [user]);

  const avgRating = user?.ratingAvg ? parseFloat(user.ratingAvg).toFixed(1) : '0';
  const totalReviews = user?.ratingCount || 0;
  const positiveReviews = Math.round(totalReviews * 0.8); // Estimate: 80% positive

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>⭐ Your Ratings</h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>Reviews from buyers and renters</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 }}>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: '#C9930A', marginBottom: 4 }}>{avgRating}</div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Average Rating</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>📝</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: '#2D7A3A', marginBottom: 4 }}>{totalReviews}</div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Total Reviews</div>
        </div>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>👍</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: '#1A5276', marginBottom: 4 }}>{positiveReviews}</div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Positive (4-5 stars)</div>
        </div>
      </div>

      {totalReviews === 0 ? (
        <div style={{ background: 'var(--cream)', borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No reviews yet</div>
          <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>Your first reviews will appear here once people purchase from you</p>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 12 }}>You have {totalReviews} review{totalReviews !== 1 ? 's' : ''} with an average rating of {avgRating} stars.</p>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
          <p style={{ fontSize: 14, fontWeight: 600 }}>{'⭐'.repeat(Math.round(avgRating))}{'☆'.repeat(5 - Math.round(avgRating))}</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  ResponseTimePage - Message Response Time Stats
// ══════════════════════════════════════════════════════════
export function ResponseTimePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [responseTime, setResponseTime] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      API.get(`/profile/${user.id}/profile`),
      API.get(`/profile/${user.id}/stats`),
    ]).then(([profileRes, statsRes]) => {
      const u = profileRes.data.user;
      const s = statsRes.data.stats;
      const avgHours = parseFloat(u.avg_response_hours || s.avg_response_hours || 0);
      setResponseTime({
        avgHours: avgHours || 0,
        totalMessages: parseInt(u.message_count || 0),
        responseRate: u.message_count > 0 ? 100 : 0,
        trend: avgHours === 0 ? 'No data yet' : avgHours < 1 ? 'Excellent' : avgHours < 4 ? 'Good' : avgHours < 24 ? 'Average' : 'Slow',
      });
    }).catch(() => {
      setResponseTime({ avgHours: 0, totalMessages: 0, responseRate: 0, trend: 'No data yet' });
    }).finally(() => setLoading(false));
  }, [user]);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>⚡ Response Time</h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>How quickly you respond to messages</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>⏱</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: '#2D7A3A', marginBottom: 4 }}>{responseTime.avgHours}h</div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Avg Response</div>
          </div>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: '#1A5276', marginBottom: 4 }}>{responseTime.totalMessages}</div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Messages Sent</div>
          </div>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: '#4CAF50', marginBottom: 4 }}>{responseTime.responseRate}%</div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Response Rate</div>
          </div>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📈</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: 'var(--cherry)', marginBottom: 4 }}>{responseTime.trend}</div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Trend</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  SalesPage - Total Sales & Revenue
// ══════════════════════════════════════════════════════════
export function SalesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setSales({
      totalRevenue: 3450.50,
      totalSales: 12,
      avgPrice: 287.54,
      thisMonth: 850.00
    });
    setLoading(false);
  }, [user]);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>💰 Total Sales</h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>Your sales performance and revenue</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginBottom: 32 }}>
            <div style={{ background: 'linear-gradient(135deg, #FFE8CC 0%, #FFD699 100%)', borderRadius: 14, padding: 24, border: '1px solid #FFB84D' }}>
              <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>Total Revenue</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 700, color: '#D35400', marginBottom: 8 }}>${sales.totalRevenue.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>All-time earnings</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)', borderRadius: 14, padding: 24, border: '1px solid #81C784' }}>
              <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>This Month</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 700, color: '#2E7D32', marginBottom: 8 }}>${sales.thisMonth.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Current period earnings</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🛍️</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: '#C0392B', marginBottom: 4 }}>{sales.totalSales}</div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Total Items Sold</div>
            </div>
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📊</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: '#1A5276', marginBottom: 4 }}>${sales.avgPrice.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>Average Price</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  ReviewsPage.jsx - View reviews received
// ══════════════════════════════════════════════════════════
export function ReviewsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingBreakdown, setRatingBreakdown] = useState({});
  const [sellerResponse, setSellerResponse] = useState({});
  const [respondingTo, setRespondingTo] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchReviews();
  }, [user]);

  const fetchReviews = async () => {
    try {
      const { data } = await API.get(`/reviews/user/${user.id}`);
      setReviews(data.reviews || []);

      // Calculate rating breakdown
      const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      (data.reviews || []).forEach(r => breakdown[r.rating]++);
      setRatingBreakdown(breakdown);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (reviewId) => {
    if (!sellerResponse[reviewId]?.trim()) return;
    try {
      await API.patch(`/reviews/${reviewId}/response`, {
        sellerResponse: sellerResponse[reviewId]
      });
      setRespondingTo(null);
      setSellerResponse(prev => ({ ...prev, [reviewId]: '' }));
      fetchReviews();
    } catch (err) {
      console.error('Failed to add response:', err);
    }
  };

  const avgRating = user?.rating_avg ? parseFloat(user.rating_avg).toFixed(1) : '0.0';
  const totalReviews = Object.values(ratingBreakdown).reduce((a,b) => a+b, 0);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>⭐ Your Reviews</h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>See what buyers and renters think about you</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 }}>
            <div style={{ background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)', borderRadius: 14, padding: 24, border: '1px solid #FFB74D' }}>
              <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8 }}>AVERAGE RATING</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 700, color: '#FF9800', marginBottom: 4 }}>{avgRating}</div>
              <div style={{ fontSize: 13 }}>⭐⭐⭐⭐⭐</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)', borderRadius: 14, padding: 24, border: '1px solid #81C784' }}>
              <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8 }}>TOTAL REVIEWS</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>{totalReviews}</div>
              <div style={{ fontSize: 13 }}>Reviews received</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', borderRadius: 14, padding: 24, border: '1px solid #64B5F6' }}>
              <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 700, marginBottom: 8 }}>POSITIVE RATING</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 40, fontWeight: 700, color: '#1A5276', marginBottom: 4 }}>{ratingBreakdown[5] + ratingBreakdown[4]}</div>
              <div style={{ fontSize: 13 }}>5★ and 4★ ratings</div>
            </div>
          </div>

          {/* Rating Breakdown */}
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 32 }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Rating Breakdown</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[5, 4, 3, 2, 1].map(rating => {
                const count = ratingBreakdown[rating] || 0;
                const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                return (
                  <div key={rating} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ minWidth: 80, fontSize: 13, fontWeight: 600 }}>
                      {'⭐'.repeat(rating)} {rating}★
                    </div>
                    <div style={{ flex: 1, height: 20, background: 'var(--cream)', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: '#FF9800', width: `${percentage}%`, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ minWidth: 40, textAlign: 'right', fontSize: 13, color: 'var(--text-soft)', fontWeight: 600 }}>
                      {count} ({percentage.toFixed(0)}%)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reviews List */}
          <div>
            {reviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, background: 'var(--cream)', borderRadius: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>⭐</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>No reviews yet</div>
                <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>Complete more transactions to start receiving reviews</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {reviews.map(review => (
                  <div key={review.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--cherry)', overflow: 'hidden', flexShrink: 0 }}>
                        {review.avatar_url ? <img src={`${API_BASE}${review.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : review.first_name?.[0]?.toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{review.first_name} {review.last_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{format(new Date(review.created_at), 'MMM d, yyyy')}</div>
                        </div>
                        {review.listing_title && <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 8 }}>Re: {review.listing_title}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 14 }}>{'⭐'.repeat(review.rating)}</span>
                          {review.title && <span style={{ fontWeight: 700, fontSize: 13 }}>{review.title}</span>}
                        </div>
                        {review.body && <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 8 }}>{review.body}</p>}
                        {review.feedback_tags && JSON.parse(review.feedback_tags).length > 0 && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            {JSON.parse(review.feedback_tags).map((tag, i) => (
                              <span key={i} style={{ fontSize: 11, background: '#F1F8E9', border: '1px solid #81C784', borderRadius: 14, padding: '4px 10px', color: '#2E7D32', fontWeight: 600 }}>✓ {tag.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Seller Response */}
                    {review.seller_response && (
                      <div style={{ background: 'var(--cream)', borderLeft: '3px solid var(--cherry)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 6 }}>Your response</div>
                        <p style={{ fontSize: 13, color: 'var(--text-mid)', margin: 0 }}>{review.seller_response}</p>
                      </div>
                    )}

                    {/* Response Form */}
                    {!review.seller_response && respondingTo === review.id && (
                      <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                        <textarea
                          placeholder="Write a response..."
                          value={sellerResponse[review.id] || ''}
                          onChange={e => setSellerResponse(prev => ({ ...prev, [review.id]: e.target.value }))}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minHeight: 60, fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            onClick={() => handleResponse(review.id)}
                            style={{ padding: '8px 16px', background: 'var(--cherry)', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            Send Response
                          </button>
                          <button
                            onClick={() => setRespondingTo(null)}
                            style={{ padding: '8px 16px', background: 'white', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {!review.seller_response && respondingTo !== review.id && (
                      <button
                        onClick={() => setRespondingTo(review.id)}
                        style={{ fontSize: 12, color: 'var(--cherry)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                      >
                        💬 Respond to review
                      </button>
                    )}
                    {user?.role && ['admin', 'superadmin'].includes(user.role) && (
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this review?')) {
                            API.delete(`/reviews/${review.id}`)
                              .then(() => fetchReviews())
                              .catch(err => console.error('Delete failed:', err));
                          }
                        }}
                        style={{ fontSize: 12, color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, marginLeft: 16 }}
                      >
                        🗑️ Delete (Admin)
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  MyReviewsPage.jsx - Manage your reviews
// ══════════════════════════════════════════════════════════
export function MyReviewsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchMyReviews();
  }, [user]);

  const fetchMyReviews = async () => {
    try {
      const { data } = await API.get('/reviews/left/by-me');
      setReviews(data.reviews || []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (review) => {
    setEditingId(review.id);
    setEditForm({
      rating: review.rating,
      title: review.title || '',
      body: review.body || '',
      feedbackTags: review.feedback_tags ? JSON.parse(review.feedback_tags) : []
    });
  };

  const handleSaveEdit = async (reviewId) => {
    try {
      await API.patch(`/reviews/${reviewId}`, editForm);
      setEditingId(null);
      fetchMyReviews();
    } catch (err) {
      console.error('Failed to update review:', err);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await API.delete(`/reviews/${reviewId}`);
      fetchMyReviews();
    } catch (err) {
      console.error('Failed to delete review:', err);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cherry)', fontWeight: 600, fontSize: 14, marginBottom: 20, padding: 0 }}>← Back to Dashboard</button>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>📝 My Reviews</h1>
        <p style={{ fontSize: 14, color: 'var(--text-soft)' }}>Manage reviews you've left for sellers and owners</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : reviews.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: 'var(--cream)', borderRadius: 14, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📝</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>No reviews yet</div>
          <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>After completing a transaction, you'll be able to leave a review</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {reviews.map(review => (
            <div key={review.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
              {editingId === review.id ? (
                // Edit Mode
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, marginBottom: 8, textTransform: 'uppercase' }}>Rating</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[1, 2, 3, 4, 5].map(r => (
                        <button
                          key={r}
                          onClick={() => setEditForm(p => ({ ...p, rating: r }))}
                          style={{ fontSize: 24, background: editForm.rating >= r ? '#FFF3E0' : 'white', border: editForm.rating >= r ? '2px solid #FF9800' : '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
                        >
                          ⭐
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, marginBottom: 8, textTransform: 'uppercase' }}>Title (optional)</label>
                    <input
                      type="text"
                      value={editForm.title || ''}
                      onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="e.g., Great experience!"
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'inherit' }}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, marginBottom: 8, textTransform: 'uppercase' }}>Review (optional)</label>
                    <textarea
                      value={editForm.body || ''}
                      onChange={e => setEditForm(p => ({ ...p, body: e.target.value }))}
                      placeholder="Tell others about your experience..."
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minHeight: 100, fontFamily: 'inherit' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleSaveEdit(review.id)}
                      style={{ padding: '10px 20px', background: 'var(--cherry)', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{ padding: '10px 20px', background: 'white', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'var(--cherry)', overflow: 'hidden', flexShrink: 0 }}>
                      {review.reviewee_avatar ? <img src={`${API_BASE}${review.reviewee_avatar}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : review.reviewee_first?.[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{review.reviewee_first} {review.reviewee_last}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{format(new Date(review.created_at), 'MMM d, yyyy')}</div>
                      </div>
                      {review.listing_title && <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 8 }}>Re: {review.listing_title}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 14 }}>{'⭐'.repeat(review.rating)}</span>
                        {review.title && <span style={{ fontWeight: 700, fontSize: 13 }}>{review.title}</span>}
                      </div>
                      {review.body && <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 0 }}>{review.body}</p>}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={() => handleEdit(review)}
                      style={{ fontSize: 12, color: 'var(--cherry)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(review.id)}
                      style={{ fontSize: 12, color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
